import { useState, useEffect } from "react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from "recharts";

const SALES_URL = "https://www.bap.agileaccelerator.fr/optin-635821441774992933045";

const SUPABASE_URL = "https://ddzgqyvnultefhuwjlbu.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRkemdxeXZudWx0ZWZodXdqbGJ1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU1NzM3MDUsImV4cCI6MjA5MTE0OTcwNX0.fGhpy5YvJqJ-EoUEOKHASGrvU9qg7PETRzoN-ktz0WI";


// ===== TRANSLATIONS =====
const T = {
  fr: {
    tagline: "Questionnaire d'Agilité Personnelle",
    tagline2: "80 questions · 10 dimensions · ~15 minutes",
    heroDesc: "Ce questionnaire mesure votre agilité personnelle : votre capacité à accueillir l'incertitude, composer avec la complexité du monde et créer de la valeur pour vous et les autres.",
    heroNote: "Répondez spontanément. Il n'y a pas de bonnes ou mauvaises réponses.",
    start: "Démarrer →",
    adminAccess: "Accès Admin",
    infoTitle: "Vos informations",
    infoSub: "Pour personnaliser votre rapport d'agilité.",
    fields: { name: "Prénom et Nom *", email: "Email *", org: "Organisation", fn: "Fonction" },
    placeholders: { name: "Jean Dupont", email: "jean@exemple.com", org: "Votre entreprise", fn: "Manager, Coach, DRH..." },
    begin: "Commencer →",
    dimOf: (s, t) => `Dimension ${s} / ${t}`,
    answers: (n) => `${n} / 80 réponses`,
    foundatrice: "Fondatrice ★",
    notAtAll: "Pas du tout",
    totally: "Totalement",
    prev: "←",
    nextDim: "Dimension suivante →",
    seeResults: "✓ Voir mes résultats",
    completeTitle: "Questionnaire complété !",
    completeThanks: (n) => `Merci ${n}.`,
    completeBody: `Vos 80 réponses ont bien été enregistrées. Votre Eveil Agile Profile™ est en cours d'analyse.`,
    completeProgram: `Dans le cadre du programme Booster Agilité Personnelle™, vos résultats seront analysés par Jean-Claude Grosjean et partagés lors de votre entretien d'accueil. Ce rapport personnalisé constituera le point de départ de votre parcours et guidera la construction de votre phase Envol.`,
    completeWhat: "Ce que nous allons explorer ensemble",
    completeWhatBody: "Vos 10 dimensions d'agilité · Vos forces distinctives · Vos axes prioritaires de développement · La construction de votre phase Envol",
    globalScore: "Score global /100",
    coherence: "Cohérence /100",
    tabOverview: "Vue globale",
    tabDimensions: "Dimensions",
    tabAxes: "Axes stratégiques",
    tabSynthese: "Synthèse",
    radarTitle: "Radar d'agilité personnelle",
    scoresTitle: "Scores par dimension",
    impact: "💼 Impact professionnel",
    vigilance: "⚠ Point de vigilance",
    imbalance: "⚡ Déséquilibre détecté",
    profileGlobal: "Profil Global",
    top3: "💪 Vos 3 atouts principaux",
    dev3: "🌱 Vos 3 axes de développement prioritaires",
    ctaLabel: "Booster Agilité Personnelle™",
    ctaTitle: "Prêt à passer au niveau supérieur ?",
    ctaBody: "Programme de coaching individuel et formation digitale · 13 semaines · Accompagnement JC Grosjean",
    ctaBtn: "En savoir plus →",
    ctaSub: "Programme sélectif · 13 semaines · Coaching individuel inclus",
    backHome: "Retour à l'accueil",
    adminTitle: "Dashboard — Eveil Agile Profile™",
    refresh: "↻",
    quit: "Quitter",
    participants: "Participants",
    avgScore: "Score moyen",
    pioneers: "Pionniers",
    awakening: "Éveil",
    generatePDF: "⬇ Générer PDF",
    backList: "← Liste",
    imbalanceLabel: "⚡ Déséquilibre détecté",
    strengths: "💪 3 atouts principaux",
    devAxes: "🌱 3 axes de développement prioritaires",
    foundatriceLabel: "Fondatrice",
    profileNames: { pionnier: "Pionnier", avance: "Avancé", explorateur: "Explorateur", eveil: "Éveil" },
  },
  en: {
    tagline: "Personal Agility Assessment",
    tagline2: "80 questions · 10 dimensions · ~15 minutes",
    heroDesc: "This questionnaire measures your personal agility: your capacity to welcome uncertainty, navigate complexity and create value for oneself and others.",
    heroNote: "Answer spontaneously. There are no right or wrong answers.",
    start: "Get started →",
    adminAccess: "Admin Access",
    infoTitle: "Your information",
    infoSub: "To personalize your agility report.",
    fields: { name: "First and Last Name *", email: "Email *", org: "Organization", fn: "Role" },
    placeholders: { name: "John Smith", email: "john@example.com", org: "Your company", fn: "Manager, Coach, CHRO..." },
    begin: "Start →",
    dimOf: (s, t) => `Dimension ${s} of ${t}`,
    answers: (n) => `${n} / 80 answered`,
    foundatrice: "Core ★",
    notAtAll: "Not at all",
    totally: "Totally",
    prev: "←",
    nextDim: "Next dimension →",
    seeResults: "✓ See my results",
    completeTitle: "Assessment completed!",
    completeThanks: (n) => `Thank you ${n}.`,
    completeBody: "Your 80 answers have been recorded. Your Eveil Agile Profile™ is being analyzed.",
    completeProgram: "As part of the Booster Agilité Personnelle™ program, your results will be analyzed by Jean-Claude Grosjean and shared during your onboarding session. This personalized report will be the starting point of your journey.",
    completeWhat: "What we will explore together",
    completeWhatBody: "Your 10 agility dimensions · Your distinctive strengths · Your priority development areas · Your personalized Envol phase order",
    globalScore: "Global score /100",
    coherence: "Consistency /100",
    tabOverview: "Overview",
    tabDimensions: "Dimensions",
    tabAxes: "Strategic Axes",
    tabSynthese: "Summary",
    radarTitle: "Personal agility radar",
    scoresTitle: "Scores by dimension",
    impact: "💼 Professional impact",
    vigilance: "⚠ Watch out for",
    imbalance: "⚡ Imbalance detected",
    profileGlobal: "Global Profile",
    top3: "💪 Your top 3 strengths",
    dev3: "🌱 Your top 3 development areas",
    ctaLabel: "Booster Agilité Personnelle™",
    ctaTitle: "Ready to level up?",
    ctaBody: "Individual coaching & digital training program · 13 weeks · Guided by JC Grosjean",
    ctaBtn: "Learn more →",
    ctaSub: "Selective program · 13 weeks · Individual coaching included",
    backHome: "Back to home",
    adminTitle: "Dashboard — Eveil Agile Profile™",
    refresh: "↻",
    quit: "Quit",
    participants: "Participants",
    avgScore: "Average score",
    pioneers: "Pioneers",
    awakening: "Awakening",
    generatePDF: "⬇ Generate PDF",
    backList: "← Back",
    imbalanceLabel: "⚡ Imbalance detected",
    strengths: "💪 Top 3 strengths",
    devAxes: "🌱 Top 3 development areas",
    foundatriceLabel: "Core",
    profileNames: { pionnier: "Pioneer", avance: "Advanced", explorateur: "Explorer", eveil: "Awakening" },
  }
};

// ===== EN DIMENSIONS =====
const DIMENSIONS_EN = [
  {
    id: "changement", name: "Change", short: "Chng", emoji: "🔄", weight: 2, couche: "fondatrices",
    intro: "Your relationship with movement, uncertainty and transformation.",
    questions: [
      { id: "Q01", text: "I make decisions even when all the information is not available.", rev: false },
      { id: "Q03", text: "I quickly adjust my behaviors when the context evolves.", rev: false },
      { id: "Q06", text: "Frequent changes significantly drain my energy.", rev: true },
      { id: "Q07", text: "I actively anticipate possible changes in my environment.", rev: false },
      { id: "Q04", text: "I tend to hold on to familiar methods even when they lose effectiveness.", rev: true },
      { id: "Q05", text: "After a setback, I quickly bounce back with a new approach.", rev: false },
      { id: "Q02", text: "Unexpected events durably disrupt my performance.", rev: true },
      { id: "Q08", text: "When facing a new problem, I often delay taking concrete action.", rev: true },
    ]
  },
  {
    id: "valeur", name: "Value", short: "Valu", emoji: "🎯", weight: 2, couche: "fondatrices",
    intro: "Your ability to focus your efforts on what creates real impact.",
    questions: [
      { id: "Q09", text: "I prioritize my actions based on their strategic impact.", rev: false },
      { id: "Q10", text: "I sometimes complete tasks without asking whether they create real value.", rev: true },
      { id: "Q14", text: "I easily get pulled into low-priority urgencies.", rev: true },
      { id: "Q11", text: "I willingly drop certain activities to focus on what matters most.", rev: false },
      { id: "Q15", text: "I connect my daily work to a broader objective.", rev: false },
      { id: "Q12", text: "I often confuse activity with real contribution.", rev: true },
      { id: "Q13", text: "I regularly measure the concrete effects of my decisions.", rev: false },
      { id: "Q16", text: "I prefer finishing all my tasks rather than choosing the most important ones.", rev: true },
    ]
  },
  {
    id: "autonomie", name: "Autonomy", short: "Auto", emoji: "🚀", weight: 1, couche: "efficacite",
    intro: "Your ability to self-organize and act without excessive dependency.",
    questions: [
      { id: "Q17", text: "I take initiative without waiting for detailed instructions.", rev: false },
      { id: "Q18", text: "I need frequent validation before acting.", rev: true },
      { id: "Q19", text: "I effectively manage my priorities when circumstances change.", rev: false },
      { id: "Q22", text: "I prefer others to make important decisions.", rev: true },
      { id: "Q20", text: "Without a clear framework, my performance drops significantly.", rev: true },
      { id: "Q21", text: "I ask for help strategically when needed.", rev: false },
      { id: "Q23", text: "I turn the freedom I have into concrete results.", rev: false },
      { id: "Q24", text: "I postpone decisions when they involve too much responsibility.", rev: true },
    ]
  },
  {
    id: "partage", name: "Sharing", short: "Shar", emoji: "🌐", weight: 1, couche: "credibilite",
    intro: "Your contribution to collective intelligence through transparency and sharing.",
    questions: [
      { id: "Q25", text: "I make visible what I am working on.", rev: false },
      { id: "Q28", text: "I fear that too much transparency could weaken my position.", rev: true },
      { id: "Q27", text: "I spontaneously share learnings that are useful to others.", rev: false },
      { id: "Q26", text: "I filter what I share based on what I consider useful for my positioning.", rev: true },
      { id: "Q29", text: "I actively contribute to enriching collective thinking.", rev: false },
      { id: "Q30", text: "I prefer keeping my methods to myself rather than sharing them.", rev: true },
      { id: "Q32", text: "I deliberately limit what I transmit to others.", rev: true },
      { id: "Q31", text: "I value exchanges as a source of collective progress.", rev: false },
    ]
  },
  {
    id: "confiance", name: "Trust", short: "Trst", emoji: "🤝", weight: 1, couche: "credibilite",
    intro: "Your relational reliability and solidity in interdependence.",
    questions: [
      { id: "Q33", text: "My actions are aligned with my commitments.", rev: false },
      { id: "Q36", text: "I tend to excessively control others' work.", rev: true },
      { id: "Q37", text: "Others can count on me over time.", rev: false },
      { id: "Q34", text: "I frequently doubt my ability to meet my responsibilities.", rev: true },
      { id: "Q35", text: "I delegate when it is relevant.", rev: false },
      { id: "Q38", text: "I systematically mistrust others' intentions.", rev: true },
      { id: "Q39", text: "I remain stable under relational tension.", rev: false },
      { id: "Q40", text: "I often question the reliability of my professional environment.", rev: true },
    ]
  },
  {
    id: "securite", name: "Psychological Safety", short: "PSaf", emoji: "🛡️", weight: 1, couche: "collectif",
    intro: "Your freedom to be authentic without excessive fear of judgment.",
    questions: [
      { id: "Q42", text: "I prefer staying silent rather than risking appearing incompetent.", rev: true },
      { id: "Q43", text: "I openly admit my mistakes.", rev: false },
      { id: "Q44", text: "I hide my doubts to preserve my image.", rev: true },
      { id: "Q41", text: "I express disagreement even when it may create discomfort.", rev: false },
      { id: "Q45", text: "I ask questions when something is unclear.", rev: false },
      { id: "Q46", text: "I fear my mistakes will be held against me indefinitely.", rev: true },
      { id: "Q47", text: "I contribute to creating an environment where others can speak freely.", rev: false },
      { id: "Q48", text: "I limit my initiatives out of fear of judgment.", rev: true },
    ]
  },
  {
    id: "amelioration", name: "Continuous Improvement", short: "Impr", emoji: "📈", weight: 1, couche: "efficacite",
    intro: "Your learning agility and discipline of permanent growth.",
    questions: [
      { id: "Q49", text: "I draw explicit lessons from my experiences.", rev: false },
      { id: "Q52", text: "I see little value in questioning what already works.", rev: true },
      { id: "Q51", text: "I regularly step back to analyze my practices.", rev: false },
      { id: "Q56", text: "I maintain outdated habits out of comfort.", rev: true },
      { id: "Q53", text: "I turn my mistakes into structured learnings.", rev: false },
      { id: "Q50", text: "I rarely question my own way of learning.", rev: true },
      { id: "Q55", text: "I actively seek to develop relevant new skills.", rev: false },
      { id: "Q54", text: "My recent learnings struggle to anchor into my daily practices.", rev: true },
    ]
  },
  {
    id: "feedback", name: "Feedback", short: "Fdbk", emoji: "💬", weight: 1, couche: "action",
    intro: "Your engine of continuous relational adjustment.",
    questions: [
      { id: "Q57", text: "I actively seek feedback on my performance.", rev: false },
      { id: "Q60", text: "I prefer avoiding critical conversations.", rev: true },
      { id: "Q59", text: "I adjust my behaviors based on the feedback I receive.", rev: false },
      { id: "Q58", text: "Criticism triggers a defensive reaction in me.", rev: true },
      { id: "Q61", text: "I formulate precise and factual feedback.", rev: false },
      { id: "Q64", text: "I downplay the importance of negative feedback.", rev: true },
      { id: "Q63", text: "I consider feedback as a lever for growth.", rev: false },
      { id: "Q62", text: "I postpone difficult conversations.", rev: true },
    ]
  },
  {
    id: "experimentation", name: "Experimentation", short: "Expe", emoji: "🧪", weight: 1, couche: "action",
    intro: "Your intelligent boldness in the face of the unknown.",
    questions: [
      { id: "Q65", text: "I quickly test new approaches at a small scale.", rev: false },
      { id: "Q66", text: "I avoid initiatives whose outcome is uncertain.", rev: true },
      { id: "Q69", text: "I iterate progressively rather than aiming for immediate perfection.", rev: false },
      { id: "Q68", text: "I prefer waiting for solid evidence before trying.", rev: true },
      { id: "Q67", text: "I analyze the results of my attempts in order to improve.", rev: false },
      { id: "Q70", text: "I give up easily after a first failure.", rev: true },
      { id: "Q71", text: "I consider testing as a strategic tool.", rev: false },
      { id: "Q72", text: "I favor security over exploration.", rev: true },
    ]
  },
  {
    id: "travail", name: "Working Together", short: "Team", emoji: "👥", weight: 1, couche: "collectif",
    intro: "Your relational intelligence in action within the collective.",
    questions: [
      { id: "Q73", text: "I actively integrate viewpoints different from my own.", rev: false },
      { id: "Q76", text: "I struggle to collaborate with very different profiles.", rev: true },
      { id: "Q75", text: "I contribute without seeking to dominate exchanges.", rev: false },
      { id: "Q74", text: "I fully listen to the other person before formulating my response.", rev: false },
      { id: "Q77", text: "I support other team members when needed.", rev: false },
      { id: "Q78", text: "I prioritize my individual efficiency at the expense of the collective.", rev: true },
      { id: "Q79", text: "I adapt my role according to the team's needs.", rev: false },
      { id: "Q80", text: "I disengage when group dynamics become complex.", rev: true },
    ]
  },
];

const COUCHES_EN = [
  { id: "fondatrices", name: "The Foundations", emoji: "🌱", desc: "The bedrock of your personal agility", dims: ["changement", "valeur"] },
  { id: "action", name: "Enlightened Action", emoji: "🎯", desc: "Test boldly and adjust continuously", dims: ["experimentation", "feedback"] },
  { id: "efficacite", name: "Lasting Efficiency", emoji: "⚙️", desc: "Self-organize and grow permanently", dims: ["autonomie", "amelioration"] },
  { id: "credibilite", name: "Credibility", emoji: "🛡️", desc: "Relational solidity and collective contribution", dims: ["partage", "confiance"] },
  { id: "collectif", name: "Collective Intelligence", emoji: "👥", desc: "Collaborate across diverse profiles and dare authenticity", dims: ["travail", "securite"] },
];

const DIM_DATA_EN = {
  changement: {
    pionnier: { label: "Uncertainty navigator", desc: "Uncertainty is your natural terrain. You make decisions with available information, bounce back from setbacks and adjust your behaviors with fluidity. Change is a signal, never a threat.", impact: "You are a precious asset in transformation, crisis or disruption contexts. Your ability to maintain performance under pressure is rare and highly sought after.", vigilance: "Be careful not to underestimate others' need for stability. Your ease in chaos can create anxiety in those who need more structure." },
    avance: { label: "Agile adapter", desc: "You manage change with good adaptive capacity. You adjust your behaviors and maintain performance in most situations. Some highly ambiguous contexts still create friction.", impact: "You are effective in changing environments. You can handle roles requiring agility, with some limits in total chaos situations.", vigilance: "Identify your trigger situations, contexts where uncertainty still paralyzes you, and work on them specifically." },
    explorateur: { label: "Cautious in movement", desc: "You adapt but uncertainty costs you energy. Some unexpected events durably disrupt your performance. You work better with a clear framework and reference points.", impact: "In stable environments you are effective. In rapid transformation contexts, you risk exhausting yourself or slowing the collective dynamic.", vigilance: "Your tendency to wait for stabilization before acting may cause you to miss windows of opportunity. An imperfect decision on time is often better than a perfect one too late." },
    eveil: { label: "Change resistant", desc: "Change is a source of tension and destabilization for you. You need stability to perform and unexpected events durably disrupt your functioning.", impact: "In a VUCA world, this dimension is a major barrier to your performance and professional evolution. It is a priority, and one of the most transformative.", vigilance: "The main risk: waiting for everything to be certain before acting, and never acting. Start with small acts of action under uncertainty, daily." },
  },
  valeur: {
    pionnier: { label: "Impact strategist", desc: "You have an integrated strategic compass. You naturally prioritize what matters, eliminate the accessory and connect each action to a real objective. Your value discipline sets you apart.", impact: "You produce more with less energy than average. Your results orientation is visible and recognized. You are a model of intentional performance.", vigilance: "Your demand for meaning can make it difficult to accept tasks without perceived value, inevitable in any organization. Cultivate strategic flexibility." },
    avance: { label: "Results-oriented actor", desc: "You generally know how to prioritize and focus on the essential. Some situations, poorly sorted urgencies, volume pressure, still make you lose sight of what really matters.", impact: "Your productivity is good but you sometimes leave energy in low-return activities. Working on your value criteria would make a real difference.", vigilance: "Learn to say no to parasitic urgencies. What seems urgent is rarely what is important." },
    explorateur: { label: "Searching for meaning", desc: "You sometimes confuse activity and impact. Task volume overtakes their real value. You move forward without always knowing if your actions contribute to what matters.", impact: "You risk exhausting yourself in action without proportional results. This dimension, once worked on, can radically change your relationship with time and performance.", vigilance: "Start each day with this question: 'What is the most important thing I can do today?' Not the most urgent, the most important." },
    eveil: { label: "Permanently in urgency", desc: "Your relationship with value is blurred. You are often in reaction, urgency or volume without a clear link to impact. Activity takes over contribution.", impact: "This is one of the most critical dimensions: without value orientation, effort does not translate into results. This work, once opened, generates immediate gains.", vigilance: "Identify your 3 absolute priorities for the week and protect them no matter what. Everything else is secondary." },
  },
  autonomie: {
    pionnier: { label: "Free and responsible actor", desc: "You take initiatives without constant validation, manage your priorities with maturity and turn freedom into concrete results. You are naturally self-organized and know the limits of your autonomy.", impact: "You are effective in flat, agile or entrepreneurial organizations. You create value proactively without waiting to be told what to do.", vigilance: "Your strong autonomy can be perceived as a lack of alignment or communication. Make visible what you are working on." },
    avance: { label: "On the path to emancipation", desc: "You function well autonomously in familiar contexts. In new or ambiguous situations, you still seek a framework or validation. Your professional maturity is clearly progressing.", impact: "You are effective in most contexts. Working on your decision-making under uncertainty will give you access to broader responsibilities.", vigilance: "Spot the moments when you ask for validation out of habit rather than real need. Every decision made alone strengthens your autonomy muscle." },
    explorateur: { label: "Seeking a framework", desc: "You need a clear framework to perform. You function better with explicit directives and regularly seek validation before acting.", impact: "In highly structured environments you are comfortable. In agile or transformation contexts, you risk being perceived as not very proactive.", vigilance: "Watch out for the validation-seeking reflex: it can signal a lack of self-confidence more than a real need for information." },
    eveil: { label: "Waiting for direction", desc: "You still depend heavily on external directives and validations to move forward. Taking initiative exposes you to discomfort you prefer to avoid.", impact: "In a world that increasingly values initiative and self-organization, this dependency is a strong barrier to your professional evolution.", vigilance: "Start with small daily acts of autonomy. Decide one thing without asking for validation. Autonomy is built in action, not reflection." },
  },
  partage: {
    pionnier: { label: "Knowledge catalyst", desc: "You are an active contributor to collective intelligence. You share spontaneously, make information accessible and see knowledge as a collective lever, never as an individual advantage.", impact: "You amplify the performance of teams around you. Your professional generosity creates lasting loyalty and legitimacy.", vigilance: "Even in sharing, some discernment is healthy. Share where it has the most impact, without scattering yourself." },
    avance: { label: "Engaged sharer", desc: "You contribute to the collective regularly and sincerely. Some retention reflexes remain in perceived competitive contexts, but your general posture is open.", impact: "Your reputation is that of a reliable and cooperative profile. One more degree of transparency would further strengthen your leadership.", vigilance: "Observe the moments when you hold back information. Most of the time, sharing gives you more than it costs you." },
    explorateur: { label: "Selective sharing", desc: "You share conditionally. You evaluate before transmitting: who deserves what, what risk you are taking. This selectivity limits your contribution to the collective.", impact: "You are perceived as discreet, sometimes hard to read. In organizations that value transparency and cooperation, this can slow your influence.", vigilance: "Ask yourself: 'Am I holding this info out of real necessity or out of a protective habit?' The answer will often be the second." },
    eveil: { label: "Information guardian", desc: "You keep information and methods to yourself. Information is a power you protect. This posture significantly limits your impact and collective credibility.", impact: "In agile or transformation organizations, lack of transparency is quickly identified and penalizing. This is a posture to rethink in depth.", vigilance: "The fear of losing an advantage by sharing is often an illusion. In high-performing collectives, those who share gain legitimacy and influence." },
  },
  confiance: {
    pionnier: { label: "Relational anchor", desc: "You are a reliable, stable and open profile. You delegate with discernment, create a climate of safety around you and maintain coherence between your words and actions.", impact: "You create a trust ecosystem that amplifies collective performance. Others align more easily with you. This is a powerful leadership lever.", vigilance: "Your solidity can lead to emotional overinvestment in relationships. Learn to protect your relational energy." },
    avance: { label: "Trust in action", desc: "You are generally reliable and create a good relational climate. In some tension or delegation situations, control reflexes can still appear.", impact: "Your trust capital is solid. Working on your mistrust triggers will give you access to an even more influential level of leadership.", vigilance: "Spot the moments when you control what you could have delegated. Trust is practiced. It is not reserved for easy situations." },
    explorateur: { label: "Residual mistrust", desc: "Your relationship with trust is ambivalent. You want to trust but fears or past experiences push you to protect yourself. Delegation is difficult, control frequent.", impact: "This ambivalence is visible in your interactions and can slow your leadership. Working on this dimension has a strong leverage effect on your collective impact.", vigilance: "Systematic mistrust is often protection against past vulnerability. Identifying the origin of this protection is the first step to overcoming it." },
    eveil: { label: "Relational defiance", desc: "You struggle to trust others and to trust yourself. This generalized mistrust posture isolates and significantly hinders your collective effectiveness and leadership.", impact: "This is one of the most limiting dimensions in a collaborative work context. Without trust, relationships remain superficial and collective performance collapses.", vigilance: "Start with small measured acts of trust: delegate a minor task, share sensitive information with a reliable peer. Build progressively." },
  },
  securite: {
    pionnier: { label: "Courageous voice", desc: "You express yourself with authenticity, admit your mistakes and create around you an environment where others dare to take relational risks. Your presence liberates team speech.", impact: "You are a catalyst for collective psychological safety. In your teams, ideas circulate better, mistakes are addressed faster, innovation is more accessible.", vigilance: "Your freedom of speech can sometimes unsettle interlocutors less comfortable with vulnerability. Calibrate according to context." },
    avance: { label: "Assumed prudence", desc: "You express yourself well in safe contexts. Facing situations perceived as risky or judgmental, you still activate some protective mechanisms.", impact: "Your impact is good in benevolent environments. Developing your ability to express yourself in difficult contexts will strengthen your leadership in adversity.", vigilance: "Identify the contexts where you self-censor. Is it really risky, or an anticipated fear? Often, speaking up costs less than one thinks." },
    explorateur: { label: "Strategic silence", desc: "You prefer staying silent rather than risking judgment. You hide your doubts, avoid disagreements and limit your initiatives out of caution. This posture costs you energy and influence.", impact: "Your ideas remain unspoken. In teams, your silence can be misinterpreted, as disengagement or passive agreement. You miss contribution opportunities.", vigilance: "The judgment you dread is often far less severe than you imagine. Exposing yourself to it progressively is the only way to overcome it." },
    eveil: { label: "Protective silence", desc: "Fear of judgment governs your expression. You hide your mistakes, avoid disagreements and strongly limit your relational risk-taking.", impact: "This dimension is a major barrier to your development. It creates a costly facade fatigue and progressively isolates. It is a fundamental priority.", vigilance: "Start by admitting a minor mistake in a safe environment. Observe the reaction. In the vast majority of cases, authenticity is received better than you fear." },
  },
  amelioration: {
    pionnier: { label: "Permanent learner", desc: "You learn continuously and systematically. You analyze your experiences, turn your mistakes into structured learnings and know how to shed obsolete skills to acquire new ones.", impact: "Your learning agility is a rare and lasting competitive advantage. In a world where skills evolve fast, your capacity to reinvent yourself is a strategic resource.", vigilance: "Watch out for learning as an end in itself. The key is transfer into practice. Learning without changing behaviors remains sterile." },
    avance: { label: "Engaged learner", desc: "You learn regularly and draw lessons from your experiences. Your improvement discipline is present but still lacks structure to make it a true growth lever.", impact: "You progress visibly over time. Formalizing your reflective practice, personal retrospectives, learning journal, would take you to the next level.", vigilance: "The difference between a good professional and an excellent one often lies in this discipline of regular reflexivity. Put it in the agenda." },
    explorateur: { label: "Intermittent progress", desc: "You learn when the environment pushes you to, but not proactively and structurally. Your reflexivity is low and you struggle to turn mistakes into lasting learnings.", impact: "Your growth curve may progressively plateau. Without active learning, the same patterns repeat. This dimension activated has a strong pull effect on all the others.", vigilance: "Start with 10 minutes of weekly retrospective: What worked well? What could I have done differently? What am I learning?" },
    eveil: { label: "Anchored in the acquired", desc: "Continuous improvement is not yet an embodied practice. You move forward with existing skills without questioning them and struggle to turn experiences into learnings.", impact: "In a world where skills evolve fast, a low learning capacity is a serious professional risk. This dimension is an absolute priority to activate.", vigilance: "Start by identifying one obsolete habit you maintain out of comfort, and decide to replace it with something better suited to your current context." },
  },
  feedback: {
    pionnier: { label: "Feedback master", desc: "You actively seek feedback, integrate it with emotional maturity and formulate it with precision and benevolence. Feedback is a growth tool for you, never a threat.", impact: "You progress faster than your peers through this continuous adjustment loop. You also create a feedback culture around you that benefits everyone.", vigilance: "Your ease with feedback can sometimes surprise interlocutors less comfortable with it. Adapt the pace and format to their maturity." },
    avance: { label: "Active openness", desc: "You are generally open to feedback and know how to give it constructively. Some situations of direct criticism still generate a slight defensive reaction, quickly overcome.", impact: "Your relationship with feedback is an asset in most contexts. Working on your emotional regulation in difficult situations will take you to the next level.", vigilance: "Spot your defensive triggers: types of feedback, people, contexts. Awareness of these triggers is already half the work." },
    explorateur: { label: "Selective openness", desc: "You receive feedback selectively: some returns pass, others trigger defense or avoidance. Giving difficult feedback is still a significant obstacle.", impact: "Your progress is limited by the quality of your feedback loops. The difficult conversations you avoid are often those that would have the most impact on your development.", vigilance: "Avoided feedback accumulates and often comes back as latent conflicts. Prefer a difficult conversation on time to a difficult rupture too late." },
    eveil: { label: "Feedback resistance", desc: "Feedback is for you a source of tension you prefer to avoid. You downplay negative feedback, postpone difficult conversations and react defensively to criticism.", impact: "Without the ability to integrate feedback, it is difficult to improve. This resistance creates a major blind spot in your professional development.", vigilance: "Working on your relationship with error and vulnerability is the entry point for developing this dimension. Error is not failure. It is data." },
  },
  experimentation: {
    pionnier: { label: "Strategic explorer", desc: "You test, iterate and learn through action with real agility. You tolerate failure as useful data and consider experimentation as a strategic adaptation tool.", impact: "You innovate faster than your peers and adapt better to disruptions. Your relationship with failure is a rare resource in organizations still very averse to risk.", vigilance: "Watch out for experimentation without a framework: testing without success criteria or structured learning. Intelligent boldness also means knowing when to stop." },
    avance: { label: "Engaged experimenter", desc: "You experiment regularly and learn from your attempts. In very unknown or high-stakes contexts, you still wait for some guarantees before launching.", impact: "Your innovation capacity is real and recognized. One more step in your tolerance for measured risk would allow you to go further in your value creation.", vigilance: "Identify a low-risk experimentation zone where you can test faster and more often. Iteration speed is a competitive advantage." },
    explorateur: { label: "Cautious experimenter", desc: "You experiment in comfortable contexts but wait for guarantees in unknown territory. Failure is still difficult to assume and you tend to give up too quickly.", impact: "Your innovation zone is still narrow. In environments that value intelligent risk-taking, this caution can limit you significantly.", vigilance: "Ask yourself before each non-experimentation: 'What is the real cost of not trying?' It often exceeds the cost of failure." },
    eveil: { label: "Safety seeker", desc: "You favor security over exploration. You wait for solid evidence before trying and give up quickly at the first obstacle. The unknown is a source of avoidance rather than curiosity.", impact: "In a fast-changing world, refusing to experiment is a long-term risk. This dimension needs working on to remain relevant and adaptive.", vigilance: "Start with mini-experiments with zero stakes. Each small successful test widens your comfort zone and rebuilds your relationship with the unknown." },
  },
  travail: {
    pionnier: { label: "Collective orchestrator", desc: "You collaborate with intelligence and fluidity, integrate different viewpoints, support others without seeking to dominate and adapt your role to the collective's needs.", impact: "Your presence in a team improves collective performance. You create positive dynamics, manage disagreements with maturity and value diversity as a resource.", vigilance: "Your ease in collectives can lead you to absorb others' tensions. Protect your own energy. You are not responsible for the entire group's harmony." },
    avance: { label: "Engaged cooperator", desc: "You collaborate effectively in the vast majority of contexts. Some very different profiles or complex dynamics can still create friction in your functioning.", impact: "Your collective impact is positive and recognized. Working on your adaptability in tension or high-diversity situations will further strengthen your collective leadership.", vigilance: "Observe your reactions to profiles that irritate you. They are often those who have the most to offer you. Friction is sometimes the signal of complementarity." },
    explorateur: { label: "Conditional collaboration", desc: "You collaborate well in favorable conditions. In complex situations or with very different profiles, you tend to withdraw or prefer the individual.", impact: "Your collective effectiveness is limited to certain contexts. In organizations that value cross-functional collaboration and diversity, this can significantly limit your progression.", vigilance: "Identify the types of profiles or dynamics that make you disengage. That is where your most powerful development lever hides." },
    eveil: { label: "Individual preference", desc: "You prefer individual efficiency over collaboration. Divergences weigh on you, complex dynamics make you disengage and you struggle to adapt your role to the collective.", impact: "In agile or matrix organizations, this preference for the individual is a significant obstacle. Collaboration has become a fundamental competency, not an option.", vigilance: "Intentionally choose one collaborative situation per week. Observe what others bring that you would not have alone. This gradually changes the perspective." },
  },
};

const COUCHE_DATA_EN = {
  fondatrices: {
    pionnier: "The bedrock of your agility is solid. You navigate uncertainty with fluidity AND know how to focus your efforts on what creates real impact. This is a rare and powerful combination.",
    avance: "Your agility foundation is broadly solid with some development areas. These two dimensions form the base. Strengthening the weaker one will multiply everything else.",
    explorateur: "Your agility foundation is still fragile. An imbalance between your change management and your value orientation limits your full agile potential. This is where everything begins.",
    eveil: "These two pillars are a priority to build. Without mastering change and value orientation, the other dimensions struggle to develop durably.",
  },
  action: {
    pionnier: "You test boldly AND adjust continuously. This combination is the engine of intelligent innovation. You learn fast and iterate better than your peers.",
    avance: "Your capacity to act and learn through action is good. A more affirmed balance between risk-taking and integrating feedback would allow you to learn even faster.",
    explorateur: "You experiment or integrate feedback, but rarely both with equal force. This layer worked on will give you a much higher learning velocity.",
    eveil: "Enlightened action is still to be built. Testing little AND integrating little feedback creates very slow learning. A priority to work on together.",
  },
  efficacite: {
    pionnier: "You self-organize with discipline AND grow permanently. This combination generates lasting and increasing performance, rare and precious.",
    avance: "Your lasting efficiency is good in most contexts. Strengthening the weaker link of the two dimensions will give you a significant competitive advantage.",
    explorateur: "Your personal efficiency has foundations but still lacks regularity. Working on these two dimensions together creates a multiplier effect.",
    eveil: "Lasting efficiency is still under construction. External dependency and low reflexivity combine to slow your long-term performance.",
  },
  credibilite: {
    pionnier: "You build solid relational credibility: you inspire trust AND share generously. Your relational capital is an asset that others recognize and seek.",
    avance: "Your credibility is good in most relationships. Some blind spots, in sharing or in trust, still limit your full radiance.",
    explorateur: "Your relational credibility is under construction. Retention or mistrust reflexes create friction in your professional relationships.",
    eveil: "Your trust capital is low in sharing and relationship quality. This is the foundation of all influence, a priority to open.",
  },
  collectif: {
    pionnier: "You are a catalyst for collective intelligence: you collaborate with diverse profiles AND dare authenticity. You create spaces where teams dare, share and perform better.",
    avance: "Your collective impact is positive and recognized. Consolidating your collaboration in diversity or your courage of expression will give you even more influence.",
    explorateur: "You collaborate or express yourself, but rarely both with force at the same time. Collective intelligence requires both ingredients to be fully activated.",
    eveil: "Collective intelligence requires being able to express oneself AND collaborate with diverse profiles. Two skills still little developed here. A fundamental priority.",
  },
};

const COUCHE_DIVERGENCE_EN = {
  fondatrices: {
    changement_valeur: "You navigate uncertainty well but struggle to prioritize what has the most impact. Your energy adapts easily. Direct it better.",
    valeur_changement: "You know what matters but uncertainty slows your ability to act. Your compass is clear, work on your capacity to move forward despite the fog.",
  },
  action: {
    experimentation_feedback: "You test boldly but integrate little feedback. Your learning cycles remain incomplete. Continuous adjustment is the missing key.",
    feedback_experimentation: "You integrate feedback well but don't yet dare enough to experiment. You learn from others. Also learn by yourself by daring more.",
  },
  efficacite: {
    autonomie_amelioration: "You move forward alone with discipline but without truly learning from your own practice. Efficiency without reflexivity creates a glass ceiling.",
    amelioration_autonomie: "You progress and learn but still depend too much on an external framework. Your growth potential needs more autonomy to express itself fully.",
  },
  credibilite: {
    partage_confiance: "You share generously but your relationship with trust remains ambiguous. One can receive from you without truly trusting you. A paradox to resolve.",
    confiance_partage: "You inspire trust in direct relationships but hold back information, which can create mistrust at a distance and limit your influence.",
  },
  collectif: {
    travail_securite: "You collaborate well technically but express yourself little in the group. Your contribution could be much stronger if you dared your voice more.",
    securite_travail: "You dare to express yourself but struggle to truly collaborate with very different profiles. Authenticity alone is not enough. Collective adaptability is the missing complement.",
  },
};

const GLOBAL_PROFILES_EN = {
  pionnier: "You demonstrate great personal agility. You navigate complexity with fluidity, create value for yourself and others and carry a posture of continuous learning. You are a vector of transformation in your environments.",
  avance: "Your personal agility is well developed, with solid strengths and identified development areas. You have the resources to progress significantly. Targeted coaching will allow you to reach the next level of impact.",
  explorateur: "Your personal agility is developing: real assets, blind spots to work on. You have all the potential to progress rapidly. This is the right time to invest in this path.",
  eveil: "Your personal agility is awakening. Agility is a competency that is learned and built. It is not innate. You have everything to gain by investing in this path, now.",
};


async function sbRpc(fnName, params) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fnName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "apikey": SUPABASE_KEY,
      "Authorization": `Bearer ${SUPABASE_KEY}`
    },
    body: JSON.stringify(params)
  });
  if (!res.ok) return null;
  return await res.json();
}

async function sbSignIn(email, password) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "apikey": SUPABASE_KEY },
    body: JSON.stringify({ email, password })
  });
  if (!res.ok) return null;
  const d = await res.json();
  return d.access_token || null;
}

async function sbFetch(jwt) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/responses?select=*&order=created_at.desc`, {
    headers: {
      "apikey": SUPABASE_KEY,
      "Authorization": `Bearer ${jwt || SUPABASE_KEY}`
    }
  });
  if (!res.ok) return [];
  return await res.json();
}

// ===== DIMENSIONS =====
// Questions reordered to break +/- alternation. Max 2 consecutive same polarity.
const DIMENSIONS = [
  {
    id: "changement", name: "Changement", short: "Chgt", emoji: "🔄", weight: 2, couche: "fondatrices",
    intro: "Votre rapport au mouvement, à l'incertitude et à la transformation.",
    questions: [
      { id: "Q01", text: "Je prends des décisions même lorsque toutes les informations ne sont pas disponibles.", rev: false },
      { id: "Q03", text: "Je modifie rapidement mes comportements lorsque le contexte évolue.", rev: false },
      { id: "Q06", text: "Les changements fréquents réduisent fortement mon énergie.", rev: true },
      { id: "Q07", text: "J'anticipe activement les évolutions possibles de mon environnement.", rev: false },
      { id: "Q04", text: "J'ai tendance à m'accrocher à des méthodes familières même si elles perdent en efficacité.", rev: true },
      { id: "Q05", text: "Après un échec, je rebondis rapidement avec une nouvelle approche.", rev: false },
      { id: "Q02", text: "Les imprévus perturbent durablement ma performance.", rev: true },
      { id: "Q08", text: "Face à un nouveau problème, je tarde souvent à passer à l'action concrète.", rev: true },
    ]
  },
  {
    id: "valeur", name: "Valeur", short: "Valr", emoji: "🎯", weight: 2, couche: "fondatrices",
    intro: "Votre aptitude à concentrer vos efforts sur ce qui crée un impact réel.",
    questions: [
      { id: "Q09", text: "Je priorise mes actions en fonction de leur impact stratégique.", rev: false },
      { id: "Q10", text: "Je réalise parfois des tâches sans me demander si elles créent réellement de la valeur.", rev: true },
      { id: "Q14", text: "Je me laisse facilement entraîner dans des urgences peu utiles.", rev: true },
      { id: "Q11", text: "Je renonce volontairement à certaines activités pour me concentrer sur l'essentiel.", rev: false },
      { id: "Q15", text: "Je relie mon travail quotidien à un objectif plus large.", rev: false },
      { id: "Q12", text: "Je confonds souvent activité et contribution réelle.", rev: true },
      { id: "Q13", text: "Je mesure régulièrement les effets concrets de mes décisions.", rev: false },
      { id: "Q16", text: "Je préfère terminer toutes mes tâches plutôt que choisir les plus importantes.", rev: true },
    ]
  },
  {
    id: "autonomie", name: "Autonomie", short: "Auto", emoji: "🚀", weight: 1, couche: "efficacite",
    intro: "Votre capacité à vous auto-organiser et agir sans dépendance excessive.",
    questions: [
      { id: "Q17", text: "Je prends des initiatives sans attendre d'instructions détaillées.", rev: false },
      { id: "Q18", text: "J'ai besoin de validation fréquente avant d'agir.", rev: true },
      { id: "Q19", text: "Je régule efficacement mes priorités lorsque les circonstances changent.", rev: false },
      { id: "Q22", text: "Je préfère que d'autres prennent les décisions importantes.", rev: true },
      { id: "Q20", text: "Sans cadre clair, ma performance diminue fortement.", rev: true },
      { id: "Q21", text: "Je demande de l'aide de manière stratégique lorsque nécessaire.", rev: false },
      { id: "Q23", text: "Je transforme la liberté dont je dispose en résultats concrets.", rev: false },
      { id: "Q24", text: "Je reporte mes décisions lorsqu'elles impliquent trop de responsabilité.", rev: true },
    ]
  },
  {
    id: "partage", name: "Partage", short: "Part", emoji: "🌐", weight: 1, couche: "credibilite",
    intro: "Votre contribution à l'intelligence collective par la transparence et le partage.",
    questions: [
      { id: "Q25", text: "Je rends visible ce sur quoi je travaille.", rev: false },
      { id: "Q28", text: "Je crains que trop de transparence puisse me fragiliser.", rev: true },
      { id: "Q27", text: "Je partage spontanément mes apprentissages utiles aux autres.", rev: false },
      { id: "Q26", text: "Je filtre ce que je partage selon ce que j'estime utile pour mon positionnement.", rev: true },
      { id: "Q29", text: "Je contribue activement à enrichir la réflexion collective.", rev: false },
      { id: "Q30", text: "Je préfère conserver mes méthodes plutôt que les diffuser.", rev: true },
      { id: "Q32", text: "Je limite volontairement ce que je transmets aux autres.", rev: true },
      { id: "Q31", text: "Je valorise les échanges comme source de progrès collectif.", rev: false },
    ]
  },
  {
    id: "confiance", name: "Confiance", short: "Conf", emoji: "🤝", weight: 1, couche: "credibilite",
    intro: "Votre fiabilité relationnelle et votre solidité dans l'interdépendance.",
    questions: [
      { id: "Q33", text: "Mes actions sont alignées avec mes engagements.", rev: false },
      { id: "Q36", text: "J'ai tendance à contrôler excessivement le travail des autres.", rev: true },
      { id: "Q37", text: "Les autres peuvent compter sur moi dans la durée.", rev: false },
      { id: "Q34", text: "Je doute fréquemment de ma capacité à tenir mes responsabilités.", rev: true },
      { id: "Q35", text: "Je délègue lorsque cela est pertinent.", rev: false },
      { id: "Q38", text: "Je me méfie systématiquement des intentions des autres.", rev: true },
      { id: "Q39", text: "Je reste stable face aux tensions relationnelles.", rev: false },
      { id: "Q40", text: "Je remets souvent en question la fiabilité de mon entourage professionnel.", rev: true },
    ]
  },
  {
    id: "securite", name: "Sécurité Psychologique", short: "SecP", emoji: "🛡️", weight: 1, couche: "collectif",
    intro: "Votre liberté d'être authentique sans peur excessive du jugement.",
    questions: [
      { id: "Q42", text: "Je préfère me taire plutôt que risquer de paraître incompétent.", rev: true },
      { id: "Q43", text: "J'admets mes erreurs ouvertement.", rev: false },
      { id: "Q44", text: "Je masque mes doutes pour préserver mon image.", rev: true },
      { id: "Q41", text: "J'exprime mes désaccords même lorsque cela peut créer un inconfort.", rev: false },
      { id: "Q45", text: "Je pose des questions lorsque quelque chose n'est pas clair.", rev: false },
      { id: "Q46", text: "Je crains que mes erreurs soient durablement retenues contre moi.", rev: true },
      { id: "Q47", text: "Je contribue à créer un climat où les autres peuvent s'exprimer librement.", rev: false },
      { id: "Q48", text: "Je limite mes prises d'initiative par peur du jugement.", rev: true },
    ]
  },
  {
    id: "amelioration", name: "Amélioration Continue", short: "Amlr", emoji: "📈", weight: 1, couche: "efficacite",
    intro: "Votre learning agility et votre discipline d'apprentissage permanent.",
    questions: [
      { id: "Q49", text: "Je tire des enseignements explicites de mes expériences.", rev: false },
      { id: "Q52", text: "Je vois peu d'intérêt à remettre en question ce qui fonctionne.", rev: true },
      { id: "Q51", text: "Je prends régulièrement du recul pour analyser mes pratiques.", rev: false },
      { id: "Q56", text: "Je conserve des habitudes devenues obsolètes par confort.", rev: true },
      { id: "Q53", text: "Je transforme mes erreurs en apprentissages structurés.", rev: false },
      { id: "Q50", text: "Je remets rarement en question ma façon d'apprendre elle-même.", rev: true },
      { id: "Q55", text: "Je cherche activement à développer de nouvelles compétences pertinentes.", rev: false },
      { id: "Q54", text: "Mes apprentissages récents peinent à s'ancrer dans mes pratiques quotidiennes.", rev: true },
    ]
  },
  {
    id: "feedback", name: "Feedback", short: "Fdbk", emoji: "💬", weight: 1, couche: "action",
    intro: "Votre moteur d'ajustement relationnel continu.",
    questions: [
      { id: "Q57", text: "Je sollicite activement des retours sur ma performance.", rev: false },
      { id: "Q60", text: "Je préfère éviter les discussions critiques.", rev: true },
      { id: "Q59", text: "J'ajuste mes comportements à partir des retours reçus.", rev: false },
      { id: "Q58", text: "Les critiques déclenchent chez moi une réaction défensive.", rev: true },
      { id: "Q61", text: "Je formule des feedbacks précis et factuels.", rev: false },
      { id: "Q64", text: "Je minimise l'importance des retours négatifs.", rev: true },
      { id: "Q63", text: "Je considère le feedback comme un levier d'évolution.", rev: false },
      { id: "Q62", text: "Je reporte les conversations difficiles.", rev: true },
    ]
  },
  {
    id: "experimentation", name: "Expérimentation", short: "Expé", emoji: "🧪", weight: 1, couche: "action",
    intro: "Votre audace intelligente face à l'inconnu.",
    questions: [
      { id: "Q65", text: "Je teste rapidement de nouvelles approches à petite échelle.", rev: false },
      { id: "Q66", text: "J'évite les initiatives dont l'issue n'est pas certaine.", rev: true },
      { id: "Q69", text: "J'itère progressivement plutôt que viser la perfection immédiate.", rev: false },
      { id: "Q68", text: "Je préfère attendre des preuves solides avant d'essayer.", rev: true },
      { id: "Q67", text: "J'analyse les résultats de mes tentatives pour progresser.", rev: false },
      { id: "Q70", text: "Je renonce facilement après un premier échec.", rev: true },
      { id: "Q71", text: "Je considère le test comme un outil stratégique.", rev: false },
      { id: "Q72", text: "Je privilégie la sécurité à l'exploration.", rev: true },
    ]
  },
  {
    id: "travail", name: "Travail Ensemble", short: "TrvE", emoji: "👥", weight: 1, couche: "collectif",
    intro: "Votre intelligence relationnelle en action dans le collectif.",
    questions: [
      { id: "Q73", text: "J'intègre activement des points de vue différents du mien.", rev: false },
      { id: "Q76", text: "J'ai du mal à collaborer avec des profils très différents.", rev: true },
      { id: "Q75", text: "Je contribue sans chercher à dominer les échanges.", rev: false },
      { id: "Q74", text: "J'écoute pleinement l'autre avant de formuler ma réponse.", rev: false },
      { id: "Q77", text: "Je soutiens les autres membres de l'équipe lorsque nécessaire.", rev: false },
      { id: "Q78", text: "Je privilégie mon efficacité individuelle au détriment du collectif.", rev: true },
      { id: "Q79", text: "J'adapte mon rôle selon les besoins de l'équipe.", rev: false },
      { id: "Q80", text: "Je me désengage lorsque les dynamiques deviennent complexes.", rev: true },
    ]
  },
];

// ===== COUCHES (corrected) =====
const COUCHES = [
  { id: "fondatrices", name: "Les Fondatrices", emoji: "🌱", desc: "Le socle de votre agilité personnelle", dims: ["changement", "valeur"] },
  { id: "action", name: "Action Éclairée", emoji: "🎯", desc: "Tester avec audace et ajuster en continu", dims: ["experimentation", "feedback"] },
  { id: "efficacite", name: "Efficacité Durable", emoji: "⚙️", desc: "S'auto-organiser et progresser en permanence", dims: ["autonomie", "amelioration"] },
  { id: "credibilite", name: "Crédibilité", emoji: "🛡️", desc: "Solidité relationnelle et contribution collective", dims: ["partage", "confiance"] },
  { id: "collectif", name: "Intelligence Collective", emoji: "👥", desc: "Collaborer avec des profils variés et oser l'authenticité", dims: ["travail", "securite"] },
];

// ===== PROFILE SYSTEM (4 levels) =====
function getProfile(score) {
  if (score >= 8.5) return "pionnier";
  if (score >= 6.5) return "avance";
  if (score >= 4.0) return "explorateur";
  return "eveil";
}
function getGlobalProfile(score) {
  if (score >= 85) return "pionnier";
  if (score >= 65) return "avance";
  if (score >= 40) return "explorateur";
  return "eveil";
}
const PROFILE_NAME = { pionnier: "Pionnier", avance: "Avancé", explorateur: "Explorateur", eveil: "Éveil" };
const PROFILE_COLOR = { pionnier: "#16a34a", avance: "#0891b2", explorateur: "#d97706", eveil: "#dc2626" };

// ===== 40 DIMENSION QUALIFIERS + DESCRIPTIONS =====
const DIM_DATA = {
  changement: {
    pionnier: {
      label: "Navigateur de l'incertitude",
      desc: "L'incertitude est votre terrain naturel. Vous prenez des décisions avec les informations disponibles, rebondissez après les échecs et ajustez vos comportements avec fluidité. Le changement est un signal, jamais une menace.",
      impact: "Vous êtes un atout précieux dans les contextes de transformation, de crise ou de disruption. Votre capacité à maintenir la performance sous pression est rare et très recherchée.",
      vigilance: "Attention à ne pas sous-estimer le besoin de stabilité des autres. Votre aisance dans le chaos peut créer de l'anxiété chez ceux qui ont besoin de plus de repères.",
    },
    avance: {
      label: "Adaptateur agile",
      desc: "Vous gérez le changement avec une bonne capacité d'adaptation. Vous ajustez vos comportements et maintenez votre performance dans la plupart des situations. Quelques contextes d'ambiguïté forte créent encore une friction.",
      impact: "Vous êtes efficace dans des environnements changeants. Vous pouvez tenir des rôles nécessitant de l'agilité, avec quelques limites dans les situations de chaos total.",
      vigilance: "Identifiez vos situations déclencheuses, les contextes où l'incertitude vous paralyse encore, et travaillez-les spécifiquement.",
    },
    explorateur: {
      label: "Prudent face au mouvement",
      desc: "Vous vous adaptez mais l'incertitude vous coûte de l'énergie. Certains imprévus perturbent votre performance de manière durable. Vous avancez mieux avec un cadre et des repères clairs.",
      impact: "Dans des environnements stables, vous êtes efficace. Dans des contextes de transformation rapide, vous risquez de vous épuiser ou de freiner la dynamique collective.",
      vigilance: "Votre tendance à attendre la stabilisation avant d'agir peut vous faire rater des fenêtres d'opportunité. La décision imparfaite à temps vaut souvent mieux que la décision parfaite trop tard.",
    },
    eveil: {
      label: "Résistant au changement",
      desc: "Le changement est pour vous source de tension et de déstabilisation. Vous avez besoin de stabilité pour performer et les imprévus perturbent durablement votre fonctionnement.",
      impact: "Dans un monde VUCA, cette dimension est un frein majeur à votre performance et à votre évolution professionnelle. C'est un chantier prioritaire, et l'un des plus transformateurs.",
      vigilance: "Le risque principal : attendre que tout soit certain avant d'agir, et ne jamais agir. Commencez par de petits gestes d'action dans l'incertitude, quotidiennement.",
    },
  },
  valeur: {
    pionnier: {
      label: "Stratège de l'impact",
      desc: "Vous avez une boussole stratégique intégrée. Vous priorisez naturellement ce qui compte, éliminez l'accessoire et reliez chaque action à un objectif réel. Votre discipline de valeur vous distingue.",
      impact: "Vous produisez davantage avec moins d'énergie que la moyenne. Votre orientation résultats est visible et reconnue. Vous êtes un modèle de performance intentionnelle.",
      vigilance: "Votre exigence de sens peut rendre difficile l'acceptation de tâches sans valeur perçue, inévitables dans toute organisation. Cultivez la flexibilité stratégique.",
    },
    avance: {
      label: "Acteur orienté résultats",
      desc: "Vous savez globalement prioriser et vous concentrer sur l'essentiel. Quelques situations, urgences mal triées, pression de volume, vous font encore perdre de vue ce qui compte vraiment.",
      impact: "Votre productivité est bonne mais vous laissez parfois de l'énergie dans des activités à faible retour. Un travail de clarification de vos critères de valeur ferait la différence.",
      vigilance: "Apprenez à dire non aux urgences parasites. Ce qui semble urgent est rarement ce qui est important.",
    },
    explorateur: {
      label: "À la recherche de sens",
      desc: "Vous confondez parfois activité et impact. Le volume de tâches prend le dessus sur leur valeur réelle. Vous avancez sans toujours savoir si vos actions contribuent à ce qui compte.",
      impact: "Vous risquez de vous épuiser dans l'action sans résultats proportionnels. Cette dimension, travaillée, peut radicalement changer votre rapport au temps et à la performance.",
      vigilance: "Commencez chaque journée par cette question : 'Quelle est la chose la plus importante que je peux faire aujourd'hui ?' Pas la plus urgente, la plus importante.",
    },
    eveil: {
      label: "Dans l'urgence permanente",
      desc: "Votre rapport à la valeur est flou. Vous êtes souvent dans la réaction, l'urgence ou le volume sans lien clair avec l'impact. L'activité prend le pas sur la contribution.",
      impact: "C'est l'une des dimensions les plus critiques : sans orientation valeur, l'effort ne se traduit pas en résultats. Ce chantier, une fois ouvert, génère des gains immédiats.",
      vigilance: "Identifiez vos 3 priorités absolues de la semaine et protégez-les coûte que coûte. Le reste est secondaire.",
    },
  },
  autonomie: {
    pionnier: {
      label: "Acteur libre et responsable",
      desc: "Vous prenez des initiatives sans validation constante, gérez vos priorités avec maturité et transformez la liberté en résultats concrets. Vous êtes naturellement auto-organisé et connaissez les limites de votre autonomie.",
      impact: "Vous êtes efficace dans les organisations plates, agiles ou entrepreneuriales. Vous créez de la valeur de manière proactive sans attendre qu'on vous dise quoi faire.",
      vigilance: "Votre autonomie forte peut être perçue comme un manque d'alignement ou de communication. Rendez visible ce sur quoi vous travaillez.",
    },
    avance: {
      label: "En voie d'émancipation",
      desc: "Vous fonctionnez bien en autonomie dans des contextes connus. Dans les situations nouvelles ou ambiguës, vous cherchez encore un cadre ou une validation. Votre maturité professionnelle progresse clairement.",
      impact: "Vous êtes efficace dans la plupart des contextes. Travailler votre rapport à la décision dans l'incertitude vous permettra d'accéder à des responsabilités plus larges.",
      vigilance: "Repérez les moments où vous demandez une validation par habitude plutôt que par réel besoin. Chaque décision prise seule renforce votre muscle d'autonomie.",
    },
    explorateur: {
      label: "En quête de cadre",
      desc: "Vous avez besoin d'un cadre clair pour performer. Vous fonctionnez mieux avec des directives explicites et cherchez régulièrement une validation avant d'agir.",
      impact: "Dans des environnements très structurés, vous êtes à l'aise. Dans des contextes agiles ou en transformation, vous risquez d'être perçu comme peu proactif.",
      vigilance: "Attention au réflexe de demande de validation. Il peut signaler un manque de confiance en soi plus qu'un réel besoin d'information.",
    },
    eveil: {
      label: "En attente de directive",
      desc: "Vous dépendez encore fortement des directives et validations externes pour avancer. La prise d'initiative vous expose à un inconfort que vous préférez éviter.",
      impact: "Dans un monde qui valorise de plus en plus l'initiative et l'auto-organisation, cette dépendance est un frein fort à votre évolution professionnelle.",
      vigilance: "Commencez par de petits actes d'autonomie quotidiens. Décidez une chose sans demander validation. L'autonomie se construit dans l'action, pas dans la réflexion.",
    },
  },
  partage: {
    pionnier: {
      label: "Catalyseur de savoir",
      desc: "Vous êtes un contributeur actif à l'intelligence collective. Vous partagez spontanément, rendez l'information accessible et voyez la connaissance comme un levier collectif, jamais comme un avantage individuel.",
      impact: "Vous amplifiez la performance des équipes autour de vous. Votre générosité professionnelle crée de la loyauté et de la légitimité durables.",
      vigilance: "Même dans le partage, une part de discernement est saine. Partagez là où ça a le plus d'impact, sans vous disperser.",
    },
    avance: {
      label: "Partage engagé",
      desc: "Vous contribuez au collectif de manière régulière et sincère. Quelques réflexes de rétention subsistent dans des contextes de compétition perçue, mais votre posture générale est ouverte.",
      impact: "Votre réputation est celle d'un profil fiable et coopératif. Un degré supplémentaire de transparence renforcerait encore votre leadership.",
      vigilance: "Observez les moments où vous retenez une information. La plupart du temps, partager vous donne plus que ça ne vous coûte.",
    },
    explorateur: {
      label: "Partage sélectif",
      desc: "Vous partagez de manière conditionnelle. Vous évaluez avant de transmettre : qui mérite quoi, quel risque vous prenez. Cette sélectivité limite votre contribution au collectif.",
      impact: "Vous êtes perçu comme discret, parfois difficile à cerner. Dans les organisations qui valorisent la transparence et la coopération, cela peut freiner votre influence.",
      vigilance: "Demandez-vous : 'Est-ce que je retiens cette info par réelle nécessité ou par habitude de protection ?' La réponse sera souvent la seconde.",
    },
    eveil: {
      label: "Gardien de l'information",
      desc: "Vous gardez l'information et vos méthodes pour vous. L'information est un pouvoir que vous protégez. Cette posture limite significativement votre impact et votre crédibilité collective.",
      impact: "Dans les organisations agiles ou en transformation, le manque de transparence est vite identifié et pénalisant. C'est une posture à repenser en profondeur.",
      vigilance: "La peur de perdre un avantage en partageant est souvent une illusion. Dans les collectifs performants, ceux qui partagent gagnent en légitimité et en influence.",
    },
  },
  confiance: {
    pionnier: {
      label: "Pilier relationnel",
      desc: "Vous êtes un profil fiable, stable et ouvert. Vous déléguez avec discernement, créez un climat de sécurité autour de vous et maintenez la cohérence entre vos paroles et vos actes.",
      impact: "Vous créez un écosystème de confiance qui amplifie la performance collective. Les autres s'alignent plus facilement sur vous. C'est un levier de leadership puissant.",
      vigilance: "Votre solidité peut mener à un surinvestissement émotionnel dans les relations. Apprenez à protéger votre énergie relationnelle.",
    },
    avance: {
      label: "Fiabilité en action",
      desc: "Vous êtes globalement fiable et créez un bon climat relationnel. Dans certaines situations de tension ou de délégation, des réflexes de contrôle peuvent encore apparaître.",
      impact: "Votre capital confiance est solide. Un travail sur vos déclencheurs de méfiance vous permettra d'accéder à un niveau de leadership encore plus influent.",
      vigilance: "Repérez les moments où vous contrôlez ce que vous auriez pu déléguer. La confiance se pratique. Elle ne se réserve pas aux situations faciles.",
    },
    explorateur: {
      label: "Méfiance résiduelle",
      desc: "Votre rapport à la confiance est ambivalent. Vous voulez faire confiance mais des peurs ou des expériences passées vous poussent à vous protéger. La délégation est difficile, le contrôle fréquent.",
      impact: "Cette ambivalence est visible dans vos interactions et peut freiner votre leadership. Travailler cette dimension a un fort effet de levier sur votre impact collectif.",
      vigilance: "La méfiance systématique est souvent une protection contre une vulnérabilité passée. Identifier l'origine de cette protection est la première étape pour la dépasser.",
    },
    eveil: {
      label: "Défiance relationnelle",
      desc: "Vous avez du mal à faire confiance aux autres et à vous faire confiance. Cette posture de méfiance généralisée isole et freine votre efficacité collective et votre leadership.",
      impact: "C'est l'une des dimensions les plus limitantes dans un contexte de travail collaboratif. Sans confiance, les relations restent superficielles et la performance collective s'effondre.",
      vigilance: "Commencez par de petits actes de confiance mesurés : déléguez une tâche mineure, partagez une information sensible avec un pair fiable. Construisez progressivement.",
    },
  },
  securite: {
    pionnier: {
      label: "Voix courageuse",
      desc: "Vous vous exprimez avec authenticité, admettez vos erreurs et créez autour de vous un environnement où les autres osent prendre des risques relationnels. Votre présence libère la parole des équipes.",
      impact: "Vous êtes un catalyseur de sécurité psychologique collective. Dans vos équipes, les idées circulent mieux, les erreurs sont traitées plus vite, l'innovation est plus accessible.",
      vigilance: "Votre liberté de parole peut parfois déconcerter des interlocuteurs moins à l'aise avec la vulnérabilité. Dosez selon le contexte.",
    },
    avance: {
      label: "Prudence assumée",
      desc: "Vous vous exprimez bien dans des contextes sécurisants. Face à des situations perçues comme risquées ou jugeantes, vous activez encore quelques mécanismes de protection.",
      impact: "Votre impact est bon dans les environnements bienveillants. Développer votre capacité à vous exprimer dans les contextes difficiles renforcera votre leadership dans l'adversité.",
      vigilance: "Identifiez les contextes où vous vous censurez. Est-ce vraiment risqué, ou est-ce une peur anticipée ? Souvent, la prise de parole coûte moins qu'on ne le croit.",
    },
    explorateur: {
      label: "Silence stratégique",
      desc: "Vous préférez vous taire plutôt que de risquer le jugement. Vous masquez vos doutes, évitez les désaccords et limitez vos initiatives par précaution. Cette posture vous coûte de l'énergie et de l'influence.",
      impact: "Vos idées restent non dites. Dans les équipes, votre silence peut être mal interprété, comme désengagement ou accord passif. Vous passez à côté d'opportunités de contribution.",
      vigilance: "Le jugement que vous redoutez est souvent bien moins sévère que vous ne l'imaginez. S'y exposer progressivement est le seul chemin pour le dépasser.",
    },
    eveil: {
      label: "Silence protecteur",
      desc: "La peur du jugement gouverne votre expression. Vous masquez vos erreurs, évitez les désaccords et limitez fortement vos prises de risque relationnelles.",
      impact: "Cette dimension est un frein majeur à votre développement. Elle crée une fatigue de façade coûteuse et isole progressivement. C'est un chantier fondamental.",
      vigilance: "Commencez par admettre une erreur mineure en toute sécurité. Observez la réaction. Dans la grande majorité des cas, l'authenticité est reçue mieux que vous ne le craignez.",
    },
  },
  amelioration: {
    pionnier: {
      label: "Apprenant permanent",
      desc: "Vous apprenez de manière continue et systématique. Vous analysez vos expériences, transformez vos erreurs en apprentissages structurés et savez vous défaire de compétences obsolètes pour en acquérir de nouvelles.",
      impact: "Votre learning agility est un avantage compétitif rare et durable. Dans un monde où les compétences se périment vite, votre capacité à vous réinventer est une ressource stratégique.",
      vigilance: "Attention à l'apprentissage comme fin en soi. L'enjeu est le transfert dans la pratique. Apprendre sans changer ses comportements reste stérile.",
    },
    avance: {
      label: "Apprentissage engagé",
      desc: "Vous apprenez régulièrement et tirez des enseignements de vos expériences. Votre discipline d'amélioration est présente mais manque encore de structure pour en faire un vrai levier de croissance.",
      impact: "Vous progressez de manière visible sur la durée. Formaliser votre pratique réflexive, rétrospectives personnelles, journal d'apprentissage, vous ferait passer un cap.",
      vigilance: "La différence entre un bon professionnel et un excellent professionnel tient souvent à cette discipline de réflexivité régulière. Mettez-la en agenda.",
    },
    explorateur: {
      label: "Progression intermittente",
      desc: "Vous apprenez quand l'environnement vous y pousse, mais pas de manière proactive et structurée. Votre réflexivité est faible et vous avez du mal à transformer les erreurs en apprentissages durables.",
      impact: "Votre courbe de progression peut plafonner progressivement. Sans apprentissage actif, les mêmes schémas se répètent. Cette dimension activée a un fort effet d'entraînement sur toutes les autres.",
      vigilance: "Commencez par 10 minutes de rétrospective hebdomadaire : Qu'est-ce qui a bien fonctionné ? Qu'aurais-je pu faire autrement ? Qu'est-ce que j'apprends ?",
    },
    eveil: {
      label: "Ancré dans l'acquis",
      desc: "L'amélioration continue n'est pas encore une pratique incarnée. Vous avancez avec les acquis existants sans les questionner et peinez à transformer vos expériences en apprentissages.",
      impact: "Dans un monde où les compétences évoluent vite, une faible capacité d'apprentissage est un risque professionnel sérieux. Cette dimension est à activer en priorité absolue.",
      vigilance: "Commencez par identifier une habitude obsolète que vous maintenez par confort, et décidez de la remplacer par quelque chose de plus adapté à votre contexte actuel.",
    },
  },
  feedback: {
    pionnier: {
      label: "Maître du retour",
      desc: "Vous demandez activement du feedback, l'intégrez avec maturité émotionnelle et le formulez avec précision et bienveillance. Le retour est pour vous un outil de croissance, jamais une menace.",
      impact: "Vous progressez plus vite que vos pairs grâce à cette boucle d'ajustement continu. Vous créez aussi autour de vous une culture du feedback qui bénéficie à tous.",
      vigilance: "Votre aisance avec le feedback peut parfois surprendre des interlocuteurs moins à l'aise. Adaptez le rythme et le format selon la maturité de vos interlocuteurs.",
    },
    avance: {
      label: "Ouverture active",
      desc: "Vous êtes globalement ouvert au feedback et savez en donner de manière constructive. Quelques situations de critique directe génèrent encore une légère réaction défensive, vite surmontée.",
      impact: "Votre rapport au feedback est un atout dans la plupart des contextes. Un travail sur votre régulation émotionnelle dans les situations difficiles vous permettra de passer au niveau supérieur.",
      vigilance: "Repérez vos déclencheurs défensifs : types de feedback, personnes, contextes. La conscience de ces déclencheurs est déjà la moitié du travail.",
    },
    explorateur: {
      label: "Ouverture sélective",
      desc: "Vous recevez du feedback de manière sélective : certains retours passent, d'autres déclenchent défense ou évitement. Donner du feedback difficile est encore un obstacle significatif.",
      impact: "Votre progression est limitée par la qualité de vos boucles de retour. Les conversations difficiles que vous évitez sont souvent celles qui auraient le plus d'impact sur votre développement.",
      vigilance: "Le feedback évité s'accumule et revient souvent sous forme de conflits latents. Préférez la conversation difficile à temps à la rupture difficile trop tard.",
    },
    eveil: {
      label: "Résistance au retour",
      desc: "Le feedback est pour vous une source de tension que vous préférez éviter. Vous minimisez les retours négatifs, reportez les conversations difficiles et réagissez de manière défensive aux critiques.",
      impact: "Sans capacité à intégrer les retours, il est difficile de s'améliorer. Cette résistance crée un angle mort majeur dans votre développement professionnel.",
      vigilance: "Travailler votre rapport à l'erreur et à la vulnérabilité est le point d'entrée pour développer cette dimension. L'erreur n'est pas un échec. C'est une donnée.",
    },
  },
  experimentation: {
    pionnier: {
      label: "Explorateur stratégique",
      desc: "Vous testez, itérez et apprenez par l'action avec une vraie agilité. Vous tolérez l'échec comme une donnée utile et considérez l'expérimentation comme un outil stratégique d'adaptation.",
      impact: "Vous innovez plus vite que vos pairs et vous adaptez mieux aux disruptions. Votre rapport à l'échec est une ressource rare dans des organisations encore très aversives au risque.",
      vigilance: "Attention à l'expérimentation sans cadre : tester sans critères de succès ni apprentissage structuré. L'audace intelligente, c'est aussi savoir quand arrêter.",
    },
    avance: {
      label: "Expérimentateur engagé",
      desc: "Vous expérimentez régulièrement et apprenez de vos tentatives. Dans des contextes très inconnus ou à fort enjeu, vous attendez encore quelques garanties avant de vous lancer.",
      impact: "Votre capacité d'innovation est réelle et reconnue. Un cran supplémentaire dans votre tolérance au risque mesuré vous permettrait d'aller plus loin dans votre création de valeur.",
      vigilance: "Identifiez une zone d'expérimentation à faible risque où vous pouvez tester plus vite et plus souvent. La vitesse d'itération est un avantage compétitif.",
    },
    explorateur: {
      label: "Expérimentateur prudent",
      desc: "Vous expérimentez dans des contextes confortables mais attendez des garanties dans les territoires inconnus. L'échec reste difficile à assumer et vous avez tendance à renoncer trop tôt.",
      impact: "Votre zone d'innovation est encore étroite. Dans des environnements qui valorisent la prise de risque intelligente, cette prudence peut vous limiter significativement.",
      vigilance: "Posez-vous cette question avant chaque non-expérimentation : 'Quel est le coût réel de ne pas essayer ?' Souvent, il dépasse le coût de l'échec.",
    },
    eveil: {
      label: "Recherche de sécurité",
      desc: "Vous préférez la sécurité à l'exploration. Vous attendez des preuves solides avant d'essayer et abandonnez vite au premier obstacle. L'inconnu est source d'évitement plutôt que de curiosité.",
      impact: "Dans un monde qui change vite, refuser d'expérimenter est une prise de risque à long terme. Cette dimension est à travailler pour rester pertinent et adaptatif.",
      vigilance: "Commencez par de mini-expériences à enjeu nul. Chaque petit test réussi élargit votre zone de confort et reconstruit votre rapport à l'inconnu.",
    },
  },
  travail: {
    pionnier: {
      label: "Orchestrateur collectif",
      desc: "Vous collaborez avec intelligence et fluidité, intégrez les points de vue différents, soutenez les autres sans chercher à dominer et adaptez votre rôle aux besoins du collectif.",
      impact: "Votre présence dans une équipe améliore la performance collective. Vous créez des dynamiques positives, gérez les désaccords avec maturité et valorisez la diversité comme une ressource.",
      vigilance: "Votre facilité dans le collectif peut vous conduire à absorber les tensions des autres. Protégez votre propre énergie. Vous n'êtes pas responsable de l'harmonie de tout le groupe.",
    },
    avance: {
      label: "Coopérateur engagé",
      desc: "Vous collaborez efficacement dans la grande majorité des contextes. Certains profils très différents ou certaines dynamiques complexes peuvent encore créer des frictions dans votre fonctionnement.",
      impact: "Votre impact collectif est positif et reconnu. Travailler votre adaptabilité dans les situations de tension ou de grande diversité renforcera encore votre leadership collectif.",
      vigilance: "Observez vos réactions face aux profils qui vous irritent. Ils sont souvent ceux qui ont le plus à vous apporter. La friction est parfois le signal d'une complémentarité.",
    },
    explorateur: {
      label: "Collaboration conditionnelle",
      desc: "Vous collaborez bien dans des conditions favorables. Dans les situations complexes ou avec des profils très différents, vous avez tendance à vous retirer ou à préférer l'individuel.",
      impact: "Votre efficacité collective est limitée à certains contextes. Dans les organisations qui valorisent la collaboration transversale et la diversité, cela peut freiner votre progression.",
      vigilance: "Identifiez les types de profils ou de dynamiques qui vous font décrocher. C'est là que se cache votre levier de développement le plus puissant.",
    },
    eveil: {
      label: "Préférence individuelle",
      desc: "Vous préférez l'efficacité individuelle à la collaboration. Les divergences vous pèsent, les dynamiques complexes vous font décrocher et vous avez du mal à adapter votre rôle au collectif.",
      impact: "Dans les organisations agiles ou matricielles, cette préférence pour l'individuel est un obstacle significatif. La collaboration est devenue une compétence fondamentale, pas une option.",
      vigilance: "Choisissez intentionnellement une situation collaborative par semaine. Observez ce que les autres apportent que vous n'auriez pas seul. Cela change progressivement le regard.",
    },
  },
};

// ===== COUCHE PROFILES =====
const COUCHE_DATA = {
  fondatrices: {
    pionnier: "Le socle de votre agilité est solide. Vous naviguez dans l'incertitude avec fluidité ET savez concentrer vos efforts sur ce qui crée un impact réel. C'est une combinaison rare et puissante.",
    avance: "Votre socle d'agilité est globalement solide avec quelques zones de développement. Ces deux dimensions forment la base. Renforcer la plus faible démultipliera l'ensemble.",
    explorateur: "Votre socle d'agilité est encore fragile. Un déséquilibre entre votre gestion du changement et votre orientation valeur limite votre plein potentiel agile. C'est ici que tout commence.",
    eveil: "Ces deux piliers sont à construire en priorité. Sans maîtrise du changement et de l'orientation valeur, les autres dimensions peinent à se développer durablement.",
  },
  action: {
    pionnier: "Vous testez avec audace ET ajustez en continu. Cette combinaison est le moteur de l'innovation intelligente. Vous apprenez vite, itérez mieux que vos pairs.",
    avance: "Votre capacité à agir et à apprendre par l'action est bonne. Un équilibre plus affirmé entre prise de risque et intégration des retours vous permettrait d'apprendre encore plus vite.",
    explorateur: "Vous expérimentez ou intégrez les retours, mais rarement les deux avec la même force. Cette couche travaillée vous donnera une vélocité d'apprentissage bien supérieure.",
    eveil: "L'action éclairée est encore à construire. Tester peu ET intégrer peu les retours crée un apprentissage très lent. C'est une priorité à travailler ensemble.",
  },
  efficacite: {
    pionnier: "Vous vous auto-organisez avec discipline ET progressez en permanence. Cette combinaison génère une performance durable et croissante, rare et précieuse.",
    avance: "Votre efficacité durable est bonne dans la plupart des contextes. Renforcer le maillon le plus faible des deux dimensions vous donnera un avantage compétitif significatif.",
    explorateur: "Votre efficacité personnelle a des bases mais manque encore de régularité. Travailler ces deux dimensions ensemble crée un effet multiplicateur.",
    eveil: "L'efficacité durable est encore en construction. Dépendance externe et faible réflexivité se combinent pour freiner votre performance long terme.",
  },
  credibilite: {
    pionnier: "Vous construisez une crédibilité relationnelle solide : vous inspirez confiance ET partagez généreusement. Votre capital relationnel est un atout que les autres reconnaissent et recherchent.",
    avance: "Votre crédibilité est bonne dans la plupart des relations. Quelques zones d'ombre, dans le partage ou dans la confiance, limitent encore votre plein rayonnement.",
    explorateur: "Votre crédibilité relationnelle est en construction. Des réflexes de rétention ou de méfiance créent des frictions dans vos relations professionnelles.",
    eveil: "Votre capital confiance est faible dans le partage et dans la qualité des relations. C'est le fondement de toute influence, un chantier à ouvrir en priorité.",
  },
  collectif: {
    pionnier: "Vous êtes un catalyseur d'intelligence collective : vous collaborez avec des profils variés ET osez l'authenticité. Vous créez des espaces où les équipes osent, partagent et performent mieux.",
    avance: "Votre impact collectif est positif et reconnu. Consolider votre collaboration dans la diversité ou votre courage d'expression vous donnera encore plus d'influence.",
    explorateur: "Vous collaborez ou vous exprimez, mais rarement les deux avec force en même temps. L'intelligence collective requiert ces deux ingrédients pour s'activer pleinement.",
    eveil: "L'intelligence collective demande de pouvoir s'exprimer ET collaborer avec des profils variés. Deux compétences encore peu développées ici. Un chantier fondamental.",
  },
};

// ===== INTRA-COUCHE DIVERGENCE =====
const COUCHE_DIVERGENCE = {
  fondatrices: {
    changement_valeur: "Vous naviguez bien dans l'incertitude mais peinez à prioriser ce qui a le plus d'impact. Votre énergie s'adapte facilement. Dirigez-la mieux.",
    valeur_changement: "Vous savez ce qui compte mais l'incertitude freine votre passage à l'action. Votre boussole est claire, travaillez votre capacité à avancer malgré le brouillard.",
  },
  action: {
    experimentation_feedback: "Vous testez avec audace mais intégrez peu les retours. Vos cycles d'apprentissage restent incomplets. L'ajustement continu est la clé qui manque.",
    feedback_experimentation: "Vous intégrez bien les retours mais n'osez pas encore assez expérimenter. Vous apprenez des autres. Apprenez aussi par vous-même en osant davantage.",
  },
  efficacite: {
    autonomie_amelioration: "Vous avancez seul avec discipline mais sans vraiment apprendre de votre propre pratique. Efficacité sans réflexivité crée un plafond de verre.",
    amelioration_autonomie: "Vous progressez et apprenez mais dépendez encore trop d'un cadre externe. Votre potentiel de croissance demande plus d'autonomie pour s'exprimer pleinement.",
  },
  credibilite: {
    partage_confiance: "Vous partagez généreusement mais votre rapport à la confiance reste ambigu. On peut recevoir de vous sans vraiment vous faire confiance. Un paradoxe à résoudre.",
    confiance_partage: "Vous inspirez confiance dans la relation directe mais retenez l'information, ce qui peut créer de la méfiance à distance et limiter votre influence.",
  },
  collectif: {
    travail_securite: "Vous collaborez bien techniquement mais vous exprimez peu dans le groupe. Votre contribution pourrait être bien plus forte si vous osiez davantage votre voix.",
    securite_travail: "Vous osez vous exprimer mais peinez à vraiment collaborer avec des profils très différents. L'authenticité seule ne suffit pas. L'adaptabilité collective est le complément manquant.",
  },
};

const GLOBAL_PROFILES = {
  pionnier: "Vous faites preuve d'une grande agilité personnelle. Vous naviguez avec fluidité dans la complexité, créez de la valeur pour vous et les autres et portez une posture d'apprentissage continu. Vous êtes un vecteur de transformation dans vos environnements.",
  avance: "Votre agilité personnelle est bien développée, avec des forces solides et des zones de développement identifiées. Vous avez les ressources pour progresser significativement. Un accompagnement ciblé vous permettra de passer au niveau supérieur d'impact.",
  explorateur: "Votre agilité personnelle est en développement : des atouts réels, des angles morts à travailler. Vous avez tout le potentiel pour progresser rapidement. C'est le bon moment pour investir dans ce chemin.",
  eveil: "Votre agilité personnelle s'éveille. L'agilité est une compétence qui s'apprend et se muscle. Elle n'est pas innée. Vous avez tout à gagner à investir dans ce chemin, maintenant.",
};

// ===== SCORING =====
function adj(raw, rev) { return rev ? 11 - raw : raw; }
function calcDimScore(dimId, answers) {
  const dim = DIMENSIONS.find(d => d.id === dimId);
  const vals = dim.questions.map(q => adj(answers[q.id] || 5, q.rev));
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}
function calcGlobalScore(dimScores) {
  let tw = 0, tt = 0;
  DIMENSIONS.forEach(d => { tw += (dimScores[d.id] || 0) * d.weight; tt += d.weight; });
  return (tw / tt) * 10;
}
function calcCoucheScore(coucheId, dimScores) {
  const c = COUCHES.find(x => x.id === coucheId);
  return c.dims.map(id => dimScores[id] || 0).reduce((a, b) => a + b, 0) / c.dims.length;
}
function calcCoherence(dimScores) {
  const vals = Object.values(dimScores);
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  const sd = Math.sqrt(vals.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / vals.length);
  return Math.max(0, Math.round(100 - sd * 15));
}
function getCoucheInsight(coucheId, dimScores) {
  const couche = COUCHES.find(c => c.id === coucheId);
  const [d1, d2] = couche.dims;
  const s1 = dimScores[d1] || 0, s2 = dimScores[d2] || 0;
  if (Math.abs(s1 - s2) < 2) return null;
  const key = s1 > s2 ? `${d1}_${d2}` : `${d2}_${d1}`;
  return COUCHE_DIVERGENCE[coucheId]?.[key] || null;
}
function computeScores(answers) {
  const dimScores = {};
  DIMENSIONS.forEach(d => { dimScores[d.id] = calcDimScore(d.id, answers); });
  const coucheScores = {};
  COUCHES.forEach(c => { coucheScores[c.id] = calcCoucheScore(c.id, dimScores); });
  return { dimScores, coucheScores, globalScore: calcGlobalScore(dimScores), coherence: calcCoherence(dimScores) };
}

// ===== STYLES =====
const G = { gold: "#C49A14", amber: "#D4761A", brown: "#2D1E00", bg: "#0D0800" };
const sCard = { background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.07)", marginBottom: 14 };
const sPage = { minHeight: "100vh", background: "#f8f7f4", fontFamily: "system-ui,-apple-system,sans-serif" };
const sBarBg = { height: 6, borderRadius: 4, background: "#e5e7eb", overflow: "hidden" };
const sBar = (pct, col) => ({ height: 6, borderRadius: 4, background: col, width: `${Math.min(100, pct)}%`, transition: "width .4s" });
const sPill = col => ({ fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 600, background: col + "20", color: col });
const sInput = { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid #d1d5db", fontSize: 14, outline: "none", boxSizing: "border-box" };
const sPrimBtn = dis => ({ width: "100%", padding: 14, borderRadius: 12, border: "none", cursor: dis ? "not-allowed" : "pointer", background: dis ? "#e5e7eb" : `linear-gradient(135deg,${G.gold},${G.amber})`, color: dis ? "#9ca3af" : "#fff", fontSize: 16, fontWeight: 700 });
const sGhostBtn = { background: "none", border: "none", color: "#9ca3af", fontSize: 13, cursor: "pointer" };

function Ring({ score, max = 10, size = 80 }) {
  const p = max === 100 ? getGlobalProfile(score) : getProfile(score);
  const col = PROFILE_COLOR[p];
  const r = size / 2 - 5;
  const circ = 2 * Math.PI * r;
  const dash = (score / max) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={5} strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      <text x={size/2} y={size/2+6} textAnchor="middle" style={{ transform: `rotate(90deg)`, transformOrigin: `${size/2}px ${size/2}px` }}
        fontSize={size > 90 ? 20 : 14} fontWeight={700} fill={col}>
        {max === 100 ? Math.round(score) : score.toFixed(1)}
      </text>
    </svg>
  );
}

function AgileRadar({ dimScores, dims }) {
  const data = (dims || DIMENSIONS).map(d => ({ dim: d.short, fullName: d.name, score: +((dimScores[d.id]||0).toFixed(1)) }));
  return (
    <ResponsiveContainer width="100%" height={290}>
      <RadarChart data={data} cx="50%" cy="50%" outerRadius="68%">
        <PolarGrid stroke="#e5e7eb" />
        <PolarAngleAxis dataKey="dim" tick={{ fontSize: 10, fill: "#374151", fontWeight: 600 }} />
        <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
        <Radar dataKey="score" stroke={G.gold} fill={G.gold} fillOpacity={0.2} strokeWidth={2} />
        <Tooltip formatter={(v,n,p) => [v+"/10", p.payload.fullName]} contentStyle={{ fontSize: 12 }} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// ===== APP =====
export default function App() {
  const isAdminUrl = window.location.pathname === "/admin";
  const [view, setView] = useState(isAdminUrl ? "admin-login" : "landing");
  const [acces] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("t") || ""; }
    catch (e) { return ""; }
  });
  const [accesEtat, setAccesEtat] = useState(isAdminUrl ? "admin" : "verif");
  const [accesMotif, setAccesMotif] = useState("");
  const [lang, setLang] = useState("fr");
  const t = T[lang];
  const dims = lang === "en" ? DIMENSIONS_EN : DIMENSIONS;
  const couches = lang === "en" ? COUCHES_EN : COUCHES;
  const dimData = lang === "en" ? DIM_DATA_EN : DIM_DATA;
  const coucheData = lang === "en" ? COUCHE_DATA_EN : COUCHE_DATA;
  const coucheDivergence = lang === "en" ? COUCHE_DIVERGENCE_EN : COUCHE_DIVERGENCE;
  const globalProfiles = lang === "en" ? GLOBAL_PROFILES_EN : GLOBAL_PROFILES;
  const profileNames = t.profileNames;

  function getLangCoucheInsight(coucheId, dimScores) {
    const couche = couches.find(c => c.id === coucheId);
    const [d1, d2] = couche.dims;
    const s1 = dimScores[d1] || 0, s2 = dimScores[d2] || 0;
    if (Math.abs(s1 - s2) < 2) return null;
    const key = s1 > s2 ? `${d1}_${d2}` : `${d2}_${d1}`;
    return coucheDivergence[coucheId]?.[key] || null;
  }
  const [step, setStep] = useState(0);
  const [info, setInfo] = useState({ name: "", email: "", org: "", fn: "" });
  const [answers, setAnswers] = useState({});
  const [scores, setScores] = useState(null);
  const [tab, setTab] = useState("overview");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPw, setAdminPw] = useState("");
  const [adminErr, setAdminErr] = useState("");
  const [jwt, setJwt] = useState(null);
  const [responses, setResponses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [cohorteType, setCohorteType] = useState("client");

  useEffect(() => {
    if (isAdminUrl) return;
    let annule = false;
    (async () => {
      if (!acces) { setAccesEtat("refuse"); setAccesMotif("absent"); return; }
      const r = await sbRpc("verifier_token", { p_token: acces, p_scope: "profil" });
      if (annule) return;
      if (r && r.valide) {
        setInfo(p => ({
          name: [r.prenom, r.nom].filter(Boolean).join(" ") || p.name,
          email: r.email || p.email,
          org: r.organisation || p.org,
          fn: r.fonction || p.fn
        }));
        setCohorteType(
          r.cohorte_type === "b2c" || r.cohorte_type === "pilote" || r.cohorte_type === "testeur"
            ? "individuel"
            : "entreprise"
        );
        setAccesEtat("ok");
      } else {
        setAccesEtat("refuse");
        setAccesMotif((r && r.motif) || "inconnu");
      }
    })();
    return () => { annule = true; };
  }, [acces, isAdminUrl]);

  const dim = dims[step];
  const dimDone = dim ? dim.questions.every(q => answers[q.id] !== undefined) : false;
  const totalAnswered = Object.keys(answers).length;

  function goNext() {
    if (step < 9) { setStep(s => s + 1); try { window.scrollTo(0,0); } catch(e) {} }
    else {
      const s = computeScores(answers);
      setScores(s); saveResponse(s); setView("complete");
      try { window.scrollTo(0,0); } catch(e) {}
    }
  }

  function generatePDF(r) {
    const sc = r.scores;
    const gp = getGlobalProfile(sc.globalScore);
    const sorted = [...DIMENSIONS].sort((a,b) => (sc.dimScores[b.id]||0) - (sc.dimScores[a.id]||0));
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Eveil Agile Profile™ — ${r.name}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'DM Sans',sans-serif;color:#1f2937;background:#fff;font-size:13px;line-height:1.65;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .page{max-width:780px;margin:0 auto;padding:44px 52px}
  @media print{.page{padding:0}.no-print{display:none!important}@page{margin:1.2cm 1.5cm;size:A4}.section-break{page-break-before:always;padding-top:24px}.dim-card,.couche-card{page-break-inside:avoid}}
  .header{background:linear-gradient(135deg,#0D0800 0%,#2D1E00 100%);padding:28px 32px;border-radius:14px;margin-bottom:28px;display:flex;justify-content:space-between;align-items:flex-start}
  .header-eyebrow{font-size:10px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:#C49A14;margin-bottom:6px}
  .header h1{font-family:'Cormorant Garamond',serif;font-size:32px;font-weight:700;color:#fff;line-height:1.1;margin-bottom:6px}
  .header-sub{font-size:11px;color:#9ca3af}
  .header-right{text-align:right;padding-top:4px}
  .header-right .author{font-size:12px;font-weight:600;color:#C49A14;margin-bottom:4px}
  .header-right .site{font-size:11px;color:#9ca3af;margin-bottom:8px}
  .header-right .confid{font-size:10px;color:#6b7280;background:rgba(255,255,255,.08);padding:3px 8px;border-radius:4px}
  .scores-hero{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px}
  .score-box{background:linear-gradient(135deg,#fefce8,#fef9eb);border:1px solid #f0e8d0;border-radius:12px;padding:20px 24px;text-align:center;position:relative;overflow:hidden}
  .score-box::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#C49A14,#D4761A)}
  .score-box.coherence-box::before{background:linear-gradient(90deg,#16a34a,#059669)}
  .score-box .val{font-family:'Cormorant Garamond',serif;font-size:42px;font-weight:700;color:#C49A14;line-height:1}
  .score-box.coherence-box .val{color:#16a34a}
  .score-box .val span{font-size:18px;color:#9ca3af;font-family:'DM Sans',sans-serif;font-weight:400}
  .score-box .lbl{font-size:11px;color:#6b7280;margin-top:6px;font-weight:500}
  .profile-badge{display:inline-flex;align-items:center;gap:6px;padding:7px 20px;border-radius:24px;font-weight:600;font-size:13px;margin-bottom:20px;letter-spacing:.02em}
  .global-desc{background:linear-gradient(135deg,#fffbeb,#fef9e7);border-left:4px solid #C49A14;padding:16px 20px;border-radius:0 10px 10px 0;font-size:13px;color:#374151;line-height:1.7;margin-bottom:32px;font-style:italic}
  h2{font-family:'Cormorant Garamond',serif;font-size:22px;color:#1a0e00;font-weight:700;margin:32px 0 16px;padding-bottom:8px;border-bottom:2px solid #f0e8d0;position:relative}
  h2::after{content:'';position:absolute;bottom:-2px;left:0;width:40px;height:2px;background:#C49A14}
  h3{font-size:13px;font-weight:600;color:#374151;margin:14px 0 8px}
  .dim-row{display:flex;align-items:center;gap:10px;margin-bottom:10px;padding:8px 12px;border-radius:8px;background:#fafaf9}
  .dim-row:nth-child(even){background:#f5f4f0}
  .dim-name{width:175px;font-size:12px;color:#1f2937;font-weight:500;flex-shrink:0}
  .dim-label{font-size:10px;font-style:italic;color:#9ca3af;width:145px;flex-shrink:0}
  .bar-bg{flex:1;height:8px;background:#e5e7eb;border-radius:6px;overflow:hidden}
  .bar-fill{height:8px;border-radius:6px}
  .dim-score{width:36px;text-align:right;font-size:13px;font-weight:700;flex-shrink:0}
  .dim-card{border:1px solid #ede9e0;border-radius:12px;padding:20px;margin-bottom:14px;page-break-inside:avoid;position:relative;overflow:hidden}
  .dim-card-accent{position:absolute;top:0;left:0;bottom:0;width:4px;border-radius:4px 0 0 4px}
  .dim-card-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px;padding-left:12px}
  .dim-card-title{font-size:15px;font-weight:700;color:#111827;margin-bottom:3px}
  .dim-card-label{font-size:11px;font-style:italic}
  .dim-card-score-wrap{text-align:right;flex-shrink:0}
  .dim-card-score{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:700;line-height:1}
  .dim-card-score span{font-size:13px;color:#9ca3af;font-family:'DM Sans',sans-serif;font-weight:400}
  .dim-score-bar{height:4px;background:#e5e7eb;border-radius:4px;overflow:hidden;margin-bottom:14px;margin-left:12px}
  .dim-score-bar-fill{height:4px;border-radius:4px}
  .desc-block{background:#f8f7f4;border-radius:8px;padding:13px 15px;font-size:12px;color:#374151;line-height:1.65;margin-bottom:12px;margin-left:12px}
  .two-col{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-left:12px}
  .info-box{border-radius:8px;padding:12px;font-size:11px;line-height:1.55}
  .impact-box{background:#f0f9ff;border-left:3px solid #0891b2}
  .vigil-box{background:#fffbeb;border-left:3px solid #d97706}
  .info-box-title{font-weight:700;margin-bottom:5px;font-size:10px;letter-spacing:.05em;text-transform:uppercase}
  .impact-box .info-box-title{color:#0891b2}
  .vigil-box .info-box-title{color:#d97706}
  .couche-card{border:1px solid #ede9e0;border-radius:12px;padding:18px 20px;margin-bottom:12px;page-break-inside:avoid;background:#fafaf9}
  .couche-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px}
  .couche-title{font-size:15px;font-weight:700;color:#111827}
  .couche-desc-sub{font-size:11px;color:#9ca3af;margin-bottom:10px}
  .couche-dims{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px}
  .couche-dim-tag{font-size:11px;background:#fff;border:1px solid #e5e7eb;padding:3px 10px;border-radius:8px}
  .couche-score{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:700}
  .couche-bar{height:5px;background:#e5e7eb;border-radius:4px;overflow:hidden;margin-bottom:12px}
  .couche-bar-fill{height:5px;border-radius:4px}
  .couche-desc-block{background:#fff;border-radius:8px;padding:12px 14px;font-size:12px;color:#374151;line-height:1.65;border:1px solid #f0ece4}
  .insight-box{background:#fef3c7;border-left:3px solid #f59e0b;border-radius:0 8px 8px 0;padding:10px 14px;margin-top:10px;font-size:11px;color:#374151;line-height:1.55}
  .synth-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px}
  .synth-block{background:#fafaf9;border:1px solid #ede9e0;border-radius:12px;padding:18px}
  .synth-block-title{font-size:13px;font-weight:700;color:#374151;margin-bottom:14px;padding-bottom:8px;border-bottom:1px solid #f0ece4}
  .atout-row{display:flex;align-items:center;gap:10px;margin-bottom:10px}
  .atout-bar{flex:1}
  .atout-bar-label{font-size:12px;font-weight:600;color:#1f2937;margin-bottom:3px;display:flex;justify-content:space-between}
  .atout-bar-bg{height:5px;background:#e5e7eb;border-radius:4px;overflow:hidden}
  .atout-bar-fill{height:5px;border-radius:4px}
  .footer{margin-top:44px;padding-top:18px;border-top:1px solid #f0e8d0;display:flex;justify-content:space-between;align-items:center;font-size:10px;color:#9ca3af}
  .footer-brand{font-weight:600;color:#C49A14}
  .print-btn{position:fixed;top:20px;right:20px;background:linear-gradient(135deg,#C49A14,#D4761A);color:#fff;border:none;padding:11px 22px;border-radius:10px;cursor:pointer;font-size:13px;font-weight:600;box-shadow:0 4px 16px rgba(196,154,20,.35);z-index:999;font-family:'DM Sans',sans-serif}
  .section-break{page-break-before:always;padding-top:8px}
</style></head><body>
<button class="print-btn no-print" onclick="window.print()">⬇ Imprimer / Exporter PDF</button>
<div class="page">

  <div class="header">
    <div>
      <div class="header-eyebrow">Eveil Agile Profile™</div>
      <h1>${r.name}</h1>
      <div class="header-sub">${[r.fn, r.org].filter(Boolean).join(' · ')} &nbsp;|&nbsp; ${r.date}</div>
    </div>
    <div class="header-right">
      <div class="author">Jean-Claude Grosjean</div>
      <div class="site">eveilagile.com</div>
      <div class="confid">Confidentiel — Usage coaching</div>
    </div>
  </div>

  <div class="scores-hero">
    <div class="score-box">
      <div class="val">${Math.round(sc.globalScore)}<span>/100</span></div>
      <div class="lbl">Score global d'agilité personnelle</div>
    </div>
    <div class="score-box coherence-box">
      <div class="val">${sc.coherence}<span>/100</span></div>
      <div class="lbl">Cohérence du profil</div>
    </div>
  </div>

  <div style="margin-bottom:16px">
    <span class="profile-badge" style="background:${PROFILE_COLOR[gp]}20;color:${PROFILE_COLOR[gp]};border:1px solid ${PROFILE_COLOR[gp]}40">${PROFILE_NAME[gp]}</span>
  </div>
  <div class="global-desc">${GLOBAL_PROFILES[gp]}</div>

  <h2>Vue d'ensemble — Scores par dimension</h2>
  ${DIMENSIONS.map(d => {
    const sc2 = sc.dimScores[d.id]||0;
    const p = getProfile(sc2);
    const col = PROFILE_COLOR[p];
    const pct = Math.min(100, sc2*10);
    return `<div class="dim-row">
      <div class="dim-name">${d.emoji} ${d.name}${d.weight===2?' ★':''}</div>
      <div class="dim-label">${DIM_DATA[d.id][p].label}</div>
      <div class="bar-bg"><div class="bar-fill" style="width:${pct}%;background:${col}"></div></div>
      <div class="dim-score" style="color:${col}">${sc2.toFixed(1)}</div>
    </div>`;
  }).join('')}

  <h2 class="section-break">Analyse détaillée par dimension</h2>
  ${DIMENSIONS.map(d => {
    const sc2 = sc.dimScores[d.id]||0;
    const p = getProfile(sc2);
    const col = PROFILE_COLOR[p];
    const dd = dimData[d.id][p];
    const pct2 = Math.min(100, sc2*10);
    return `<div class="dim-card">
      <div class="dim-card-accent" style="background:${col}"></div>
      <div class="dim-card-header">
        <div>
          <div class="dim-card-title">${d.emoji} ${d.name}${d.weight===2?' <span style="font-size:10px;color:#C49A14;background:#fef9ec;padding:2px 6px;border-radius:4px;margin-left:6px">Fondatrice ★</span>':''}</div>
          <div class="dim-card-label" style="color:${col}">${dd.label}</div>
        </div>
        <div class="dim-card-score-wrap">
          <div class="dim-card-score" style="color:${col}">${sc2.toFixed(1)}<span>/10</span></div>
        </div>
      </div>
      <div class="dim-score-bar"><div class="dim-score-bar-fill" style="width:${pct2}%;background:${col}"></div></div>
      <div class="desc-block">${dd.desc}</div>
      <div class="two-col">
        <div class="info-box impact-box"><div class="info-box-title">💼 Impact professionnel</div>${dd.impact}</div>
        <div class="info-box vigil-box"><div class="info-box-title">⚠ Point de vigilance</div>${dd.vigilance}</div>
      </div>
    </div>`;
  }).join('')}

  <h2 class="section-break">Analyse par couche</h2>
  ${COUCHES.map(c => {
    const csc = c.dims.map(id => sc.dimScores[id]||0).reduce((a,b)=>a+b,0)/c.dims.length;
    const p = getProfile(csc);
    const col = PROFILE_COLOR[p];
    const insight = getCoucheInsight(c.id, sc.dimScores);
    const cDimsArr = DIMENSIONS.filter(d => c.dims.includes(d.id));
    const pctC = Math.min(100, csc*10);
    return `<div class="couche-card">
      <div class="couche-header">
        <div>
          <div class="couche-title">${c.emoji} ${c.name}</div>
          <div class="couche-desc-sub">${c.desc}</div>
          <div class="couche-dims">${cDimsArr.map(d=>`<span class="couche-dim-tag">${d.emoji} ${d.name} <strong style="color:${PROFILE_COLOR[getProfile(sc.dimScores[d.id]||0)]}">${(sc.dimScores[d.id]||0).toFixed(1)}</strong></span>`).join('')}</div>
        </div>
        <div class="couche-score" style="color:${col}">${csc.toFixed(1)}<span style="font-size:13px;color:#9ca3af;font-family:'DM Sans',sans-serif;font-weight:400">/10</span></div>
      </div>
      <div class="couche-bar"><div class="couche-bar-fill" style="width:${pctC}%;background:${col}"></div></div>
      <div class="couche-desc-block">${COUCHE_DATA[c.id][p]}</div>
      ${insight ? `<div class="insight-box"><strong style="color:#b45309">⚡ Déséquilibre détecté :</strong> ${insight}</div>` : ''}
    </div>`;
  }).join('')}

  <h2>Synthèse — Atouts & Axes de développement</h2>
  <div class="synth-grid">
    <div class="synth-block">
      <div class="synth-block-title">💪 Vos 3 atouts principaux</div>
      ${sorted.slice(0,3).map(d => {
        const sc2 = sc.dimScores[d.id]||0;
        const p = getProfile(sc2);
        return `<div class="atout-row">
          <span style="font-size:16px">${d.emoji}</span>
          <div class="atout-bar">
            <div class="atout-bar-label"><span>${d.name}</span><span style="color:${PROFILE_COLOR[p]}">${sc2.toFixed(1)}</span></div>
            <div style="font-size:10px;color:${PROFILE_COLOR[p]};font-style:italic;margin-bottom:4px">${DIM_DATA[d.id][p].label}</div>
            <div class="atout-bar-bg"><div class="atout-bar-fill" style="width:${Math.min(100,sc2*10)}%;background:${PROFILE_COLOR[p]}"></div></div>
          </div>
        </div>`;
      }).join('')}
    </div>
    <div class="synth-block">
      <div class="synth-block-title">🌱 3 axes de développement prioritaires</div>
      ${sorted.slice(-3).reverse().map(d => {
        const sc2 = sc.dimScores[d.id]||0;
        const p = getProfile(sc2);
        return `<div class="atout-row">
          <span style="font-size:16px">${d.emoji}</span>
          <div class="atout-bar">
            <div class="atout-bar-label"><span>${d.name}</span><span style="color:${PROFILE_COLOR[p]}">${sc2.toFixed(1)}</span></div>
            <div style="font-size:10px;color:${PROFILE_COLOR[p]};font-style:italic;margin-bottom:4px">${DIM_DATA[d.id][p].label}</div>
            <div class="atout-bar-bg"><div class="atout-bar-fill" style="width:${Math.min(100,sc2*10)}%;background:${PROFILE_COLOR[p]}"></div></div>
          </div>
        </div>`;
      }).join('')}
    </div>
  </div>

  <div class="footer">
    <div><span class="footer-brand">Eveil Agile Profile™</span> &nbsp;·&nbsp; Jean-Claude Grosjean &nbsp;·&nbsp; eveilagile.com</div>
    <div>Généré le ${new Date().toLocaleDateString('fr-FR')} &nbsp;·&nbsp; Propriété intellectuelle Eveil Agile</div>
  </div>
</div></body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  }
  function goPrev() {
    if (step > 0) { setStep(s => s - 1); try { window.scrollTo(0,0); } catch(e) {} }
    else setView("info");
  }
  async function saveResponse(s) {
    try {
      const r = await sbRpc("soumettre_reponse", {
        p_token: acces,
        p_scope: "profil",
        p_payload: {
          name: info.name,
          email: info.email,
          org: info.org,
          fn: info.fn,
          date: new Date().toLocaleDateString("fr-FR"),
          scores: s,
          answers: answers
        }
      });
      if (!r || r.ok !== true) console.error("Enregistrement refuse:", r);
    } catch(e) { console.error("Save error:", e); }
  }
  async function loadResponses(token) {
    try {
      const data = await sbFetch(token);
      const arr = data.map(r => ({
        key: r.id,
        name: r.name,
        email: r.email,
        org: r.org,
        fn: r.fn,
        date: r.date,
        scores: r.scores,
        answers: r.answers
      }));
      setResponses(arr);
    } catch(e) { console.error("Load error:", e); }
  }
  async function adminLogin() {
    setAdminErr("");
    const token = await sbSignIn(adminEmail.trim(), adminPw);
    if (token) {
      setJwt(token);
      await loadResponses(token);
      setView("admin");
    } else {
      setAdminErr("Identifiants incorrects");
    }
  }

  // LANG SWITCHER component
  const LangSwitch = () => (
    <div style={{ position: "absolute", top: 16, right: 20, display: "flex", gap: 4 }}>
      <button onClick={() => setLang("fr")} style={{ padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: lang==="fr" ? G.gold : "rgba(255,255,255,.1)", color: lang==="fr" ? "#fff" : "#9ca3af" }}>FR</button>
      <button onClick={() => setLang("en")} style={{ padding: "4px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: lang==="en" ? G.gold : "rgba(255,255,255,.1)", color: lang==="en" ? "#fff" : "#9ca3af" }}>EN</button>
    </div>
  );

  // COMPLETE — participant sees this, no results shown
  if (view === "complete") return (
    <div style={{ ...sPage, background: `linear-gradient(160deg,${G.bg} 0%,${G.brown} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, position: "relative" }}>
      <LangSwitch />
      <div style={{ maxWidth: 480, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 20 }}>✅</div>
        <h1 style={{ color: G.gold, fontSize: 24, fontWeight: 700, margin: "0 0 10px", fontFamily: "Georgia,serif" }}>{t.completeTitle}</h1>
        <p style={{ color: G.amber, fontSize: 15, margin: "0 0 28px" }}>{t.completeThanks(info.name.split(' ')[0])}</p>
        <div style={{ background: "rgba(196,154,20,.08)", border: "1px solid rgba(196,154,20,.25)", borderRadius: 16, padding: 28, textAlign: "left", marginBottom: 24 }}>
          <p style={{ color: "#e5e7eb", fontSize: 14, lineHeight: 1.8, margin: "0 0 14px" }}>
            {t.completeBody}
          </p>
          <p style={{ color: "#d1d5db", fontSize: 13, lineHeight: 1.7, margin: "0 0 14px" }}>
            {t.completeProgram}
          </p>

        </div>
        <div style={{ background: "rgba(255,255,255,.04)", borderRadius: 12, padding: "14px 20px", textAlign: "left", border: "1px solid rgba(255,255,255,.08)" }}>
          <p style={{ color: "#6b7280", fontSize: 11, margin: "0 0 6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>{t.completeWhat}</p>
          <p style={{ color: "#9ca3af", fontSize: 13, lineHeight: 1.7, margin: 0 }}>
            {t.completeWhatBody}
          </p>
        </div>
      </div>
    </div>
  );

  // LANDING
  // ===== CONTROLE D'ACCES =====
  if (!isAdminUrl && accesEtat === "verif") return (
    <div style={{ ...sPage, background: `linear-gradient(160deg,${G.bg} 0%,${G.brown} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <p style={{ color: G.amber, fontSize: 14 }}>Verification de votre acces...</p>
    </div>
  );

  if (!isAdminUrl && accesEtat === "refuse") {
    const MSG = {
      absent: "Ce diagnostic est reserve aux participants du programme Booster Agilite Personnelle et aux collaborateurs des organisations partenaires. L'acces se fait par lien personnel.",
      inconnu: "Ce lien n'est pas valide. Verifiez que vous avez bien copie l'adresse complete recue par email.",
      deja_utilise: "Ce lien a deja ete utilise. Chaque acces est personnel et ne permet qu'une seule passation.",
      expire: "Ce lien a expire. Contactez-moi pour en recevoir un nouveau.",
      revoque: "Cet acces a ete desactive.",
      pas_encore_ouvert: "Cet acces n'est pas encore ouvert. Vous recevrez une notification le moment venu."
    };
    return (
      <div style={{ ...sPage, background: `linear-gradient(160deg,${G.bg} 0%,${G.brown} 100%)`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, position: "relative" }}>
        <div style={{ maxWidth: 460, width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>⚡</div>
          <h1 style={{ color: G.gold, fontSize: 28, fontWeight: 700, margin: "0 0 6px", fontFamily: "Georgia,serif" }}>Eveil Agile Profile™</h1>
          <p style={{ color: G.amber, fontSize: 15, margin: "0 0 24px" }}>Le diagnostic des 10 dimensions de l'agilite personnelle</p>
          <div style={{ background: "rgba(196,154,20,.08)", border: "1px solid rgba(196,154,20,.25)", borderRadius: 16, padding: 22, marginBottom: 24, textAlign: "left" }}>
            <p style={{ color: "#d1d5db", fontSize: 13, lineHeight: 1.7, margin: 0 }}>{MSG[accesMotif] || MSG.inconnu}</p>
          </div>
          <p style={{ color: "#9ca3af", fontSize: 13, lineHeight: 1.7, margin: "0 0 22px" }}>
            80 questions, 15 minutes. Un radar personnalise sur les 10 dimensions, vos axes strategiques, vos points forts et vos zones de progression.
          </p>
          <a href={SALES_URL} style={{ ...sPrimBtn(false), display: "inline-block", textDecoration: "none", textAlign: "center" }}>
            Decouvrir le programme →
          </a>
          <p style={{ color: "#6b7280", fontSize: 12, margin: "18px 0 0" }}>
            Jean-Claude Grosjean · <a href="https://eveilagile.com" style={{ color: G.amber, textDecoration: "none" }}>eveilagile.com</a>
          </p>
        </div>
      </div>
    );
  }

  if (view === "landing") return (
    <div style={{ ...sPage, background: `linear-gradient(160deg,${G.bg} 0%,${G.brown} 100%)`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, position: "relative" }}>
      <LangSwitch />
      <div style={{ maxWidth: 440, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>⚡</div>
        <h1 style={{ color: G.gold, fontSize: 28, fontWeight: 700, margin: "0 0 6px", fontFamily: "Georgia,serif" }}>Eveil Agile Profile™</h1>
        <p style={{ color: G.amber, fontSize: 15, margin: "0 0 6px" }}>{t.tagline}</p>
        <p style={{ color: "#9ca3af", fontSize: 12, margin: "0 0 28px" }}>{t.tagline2}</p>
        <div style={{ background: "rgba(196,154,20,.08)", border: "1px solid rgba(196,154,20,.25)", borderRadius: 16, padding: 20, marginBottom: 24, textAlign: "left" }}>
          <p style={{ color: "#d1d5db", fontSize: 13, lineHeight: 1.7, margin: 0 }}>{t.heroDesc}</p>
          <p style={{ color: "#9ca3af", fontSize: 12, margin: "10px 0 0" }}>{t.heroNote}</p>
        </div>
        <div style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.10)", borderRadius: 16, padding: 20, marginBottom: 24, textAlign: "left" }}>
          {cohorteType === "entreprise" ? (
            <>
              <p style={{ color: G.amber, fontSize: 13, fontWeight: 700, margin: "0 0 10px" }}>
                {lang === "fr" ? "Ce que votre organisation voit, et ce qu'elle ne voit pas" : "What your organization sees, and what it does not"}
              </p>
              <p style={{ color: "#d1d5db", fontSize: 12.5, lineHeight: 1.7, margin: "0 0 10px" }}>
                {lang === "fr"
                  ? "Vos réponses question par question et votre radar détaillé restent entre vous et Jean-Claude Grosjean. Votre organisation n'y a pas accès."
                  : "Your individual answers and detailed radar stay between you and Jean-Claude Grosjean. Your organization has no access to them."}
              </p>
              <p style={{ color: "#d1d5db", fontSize: 12.5, lineHeight: 1.7, margin: "0 0 10px" }}>
                {lang === "fr"
                  ? "Elle reçoit une vision de la cohorte : moyennes par dimension, priorités collectives, évolution entre le début et la fin du parcours. Aucun résultat n'est affiché en dessous de trois répondants, afin qu'on ne puisse identifier personne dans une petite équipe."
                  : "It receives a cohort-level view: averages per dimension, collective priorities, and progress between the start and the end of the program. No result is displayed below three respondents, so that no one can be identified within a small team."}
              </p>
              <p style={{ color: "#9ca3af", fontSize: 12.5, lineHeight: 1.7, margin: 0 }}>
                {lang === "fr"
                  ? "Vous pouvez donc répondre avec franchise. C'est la seule façon d'obtenir quelque chose d'utile."
                  : "You can therefore answer candidly. That is the only way to get something useful out of it."}
              </p>
            </>
          ) : (
            <>
              <p style={{ color: G.amber, fontSize: 13, fontWeight: 700, margin: "0 0 10px" }}>
                {lang === "fr" ? "Vos réponses vous appartiennent" : "Your answers are yours"}
              </p>
              <p style={{ color: "#d1d5db", fontSize: 12.5, lineHeight: 1.7, margin: "0 0 10px" }}>
                {lang === "fr"
                  ? "Ce diagnostic est confidentiel. Vos réponses ne sont partagées avec personne et servent uniquement à votre accompagnement."
                  : "This assessment is confidential. Your answers are shared with no one and serve your coaching only."}
              </p>
              <p style={{ color: "#9ca3af", fontSize: 12.5, lineHeight: 1.7, margin: 0 }}>
                {lang === "fr"
                  ? "Répondez sur ce que vous faites réellement aujourd'hui, pas sur ce que vous voudriez faire."
                  : "Answer based on what you actually do today, not on what you would like to do."}
              </p>
            </>
          )}
        </div>
        <button onClick={() => setView("info")} style={sPrimBtn(false)}>{t.start}</button>
      </div>
    </div>
  );

  // INFO
  if (view === "info") return (
    <div style={{ ...sPage, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 420, width: "100%" }}>
        <button onClick={() => setView("landing")} style={sGhostBtn}>← Retour</button>
        <div style={{ ...sCard, marginTop: 16 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: "0 0 6px" }}>{t.infoTitle}</h2>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 22px" }}>{t.infoSub}</p>
          {[{ label: t.fields.name, k: "name", ph: t.placeholders.name }, { label: t.fields.email, k: "email", ph: t.placeholders.email }, { label: t.fields.org, k: "org", ph: t.placeholders.org }, { label: t.fields.fn, k: "fn", ph: t.placeholders.fn }].map(f => (
            <div key={f.k} style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 5 }}>{f.label}</label>
              <input style={sInput} placeholder={f.ph} value={info[f.k]} onChange={e => setInfo(p => ({ ...p, [f.k]: e.target.value }))} />
            </div>
          ))}
          <button onClick={() => { if (!info.name||!info.email) return; setStep(0); setAnswers({}); setView("questionnaire"); }} style={sPrimBtn(!info.name||!info.email)} disabled={!info.name||!info.email}>
            {t.begin}
          </button>
        </div>
      </div>
    </div>
  );

  // QUESTIONNAIRE
  if (view === "questionnaire") {
    const pct = ((step + (dimDone ? 1 : 0)) / 10) * 100;
    return (
      <div style={sPage}>
        <div style={{ position: "sticky", top: 0, background: "#fff", borderBottom: "1px solid #f0f0f0", padding: "12px 20px", zIndex: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>{t.dimOf(step+1, 10)}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: G.gold }}>{t.answers(totalAnswered)}</span>
          </div>
          <div style={sBarBg}><div style={sBar(pct + 10, `linear-gradient(90deg,${G.gold},${G.amber})`)} /></div>
        </div>
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "16px 14px" }}>
          <div style={sCard}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>{dim.emoji}</span>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>{dim.name}</h2>
                  {dim.weight === 2 && <span style={sPill(G.gold)}>Fondatrice ★</span>}
                </div>
                <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 0" }}>{dim.intro}</p>
              </div>
            </div>
          </div>
          {dim.questions.map(q => (
            <div key={q.id} style={sCard}>
              <p style={{ fontSize: 14, fontWeight: 500, color: "#1f2937", margin: "0 0 14px", lineHeight: 1.5 }}>
                <span style={{ fontSize: 10, color: "#c4b5a0", marginRight: 6 }}>{q.id}</span>{q.text}
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(10,1fr)", gap: 3 }}>
                {[1,2,3,4,5,6,7,8,9,10].map(v => (
                  <button key={v} onClick={() => setAnswers(p => ({ ...p, [q.id]: v }))} style={{
                    padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                    background: answers[q.id] === v ? G.gold : "#f3f4f6",
                    color: answers[q.id] === v ? "#fff" : "#374151",
                    transform: answers[q.id] === v ? "scale(1.08)" : "scale(1)", transition: "all .12s"
                  }}>{v}</button>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                <span style={{ fontSize: 10, color: "#9ca3af" }}>{t.notAtAll}</span>
                <span style={{ fontSize: 10, color: "#9ca3af" }}>{t.totally}</span>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, marginBottom: 28 }}>
            <button onClick={goPrev} style={{ padding: "12px 18px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", fontWeight: 600, color: "#374151", fontSize: 14 }}>←</button>
            <button onClick={goNext} disabled={!dimDone} style={{ ...sPrimBtn(!dimDone), width: "auto", flex: 1, fontSize: 14 }}>
              {step === 9 ? t.seeResults : t.nextDim}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // RESULTS
  if (view === "results" && scores) {
    const { dimScores, coucheScores, globalScore, coherence } = scores;
    const gp = getGlobalProfile(globalScore);
    const sorted = [...dims].sort((a,b) => (dimScores[b.id]||0) - (dimScores[a.id]||0));

    return (
      <div style={sPage}>
        <div style={{ background: `linear-gradient(135deg,${G.bg},${G.brown})`, padding: "28px 24px 20px", textAlign: "center" }}>
          <p style={{ color: G.amber, fontSize: 12, margin: "0 0 3px" }}>Eveil Agile Profile™</p>
          <h1 style={{ color: G.gold, fontSize: 20, fontWeight: 700, margin: "0 0 2px", fontFamily: "Georgia,serif" }}>{info.name}</h1>
          {(info.org||info.fn) && <p style={{ color: "#9ca3af", fontSize: 11, margin: "0 0 18px" }}>{[info.fn,info.org].filter(Boolean).join(" · ")}</p>}
          <div style={{ display: "flex", justifyContent: "center", gap: 24, alignItems: "center", marginBottom: 12 }}>
            <div style={{ textAlign: "center" }}><Ring score={globalScore} max={100} size={94} /><p style={{ color: "#9ca3af", fontSize: 10, margin: "4px 0 0" }}>{t.globalScore}</p></div>
            <div style={{ textAlign: "center" }}><Ring score={coherence} max={100} size={74} /><p style={{ color: "#9ca3af", fontSize: 10, margin: "4px 0 0" }}>{t.coherence}</p></div>
          </div>
          <span style={{ ...sPill(PROFILE_COLOR[gp]), fontSize: 12, padding: "5px 16px" }}>{profileNames[gp]}</span>
        </div>

        <div style={{ display: "flex", background: "#fff", borderBottom: "1px solid #f0f0f0", overflowX: "auto" }}>
          {[["overview","Vue globale"],["dimensions","Dimensions"],["couches","Couches"],["synthese","Synthèse"]].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: "12px 16px", border: "none", borderBottom: `2px solid ${tab===id ? G.gold : "transparent"}`, background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: tab===id ? G.gold : "#6b7280", whiteSpace: "nowrap" }}>{label}</button>
          ))}
        </div>

        <div style={{ maxWidth: 680, margin: "0 auto", padding: "14px" }}>
          {tab === "overview" && (
            <div>
              <div style={sCard}><h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: "#111827" }}>{t.radarTitle}</h3><AgileRadar dimScores={dimScores} dims={dims} /></div>
              <div style={sCard}>
                <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#111827" }}>{t.scoresTitle}</h3>
                {dims.map(d => {
                  const sc = dimScores[d.id]||0;
                  const p = getProfile(sc);
                  const dd = dimData[d.id][p];
                  return (
                    <div key={d.id} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, alignItems: "center" }}>
                        <span style={{ fontSize: 13, color: "#374151", display: "flex", alignItems: "center", gap: 6 }}>
                          {d.emoji} {d.name} {d.weight===2 && <span style={{ color: G.gold, fontSize: 11 }}>★</span>}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 10, color: PROFILE_COLOR[p], fontStyle: "italic" }}>{dd.label}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: PROFILE_COLOR[p] }}>{sc.toFixed(1)}</span>
                        </div>
                      </div>
                      <div style={sBarBg}><div style={sBar(sc*10, PROFILE_COLOR[p])} /></div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "dimensions" && dims.map(d => {
            const sc = dimScores[d.id]||0;
            const p = getProfile(sc);
            const col = PROFILE_COLOR[p];
            const dd = dimData[d.id][p];
            return (
              <div key={d.id} style={sCard}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 22 }}>{d.emoji}</span>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{d.name}</span>
                        {d.weight===2 && <span style={{ ...sPill(G.gold), fontSize: 10 }}>{t.foundatriceLabel}</span>}
                      </div>
                      <span style={{ fontSize: 12, fontStyle: "italic", color: col }}>{dd.label}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}><Ring score={sc} max={10} size={58} /></div>
                </div>
                <div style={sBarBg}><div style={sBar(sc*10, col)} /></div>
                <div style={{ background: col+"12", borderRadius: 10, padding: 14, marginTop: 12 }}>
                  <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.65, margin: 0 }}>{dd.desc}</p>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                  <div style={{ background: "#f0f9ff", borderRadius: 10, padding: 12, borderLeft: "3px solid #0891b2" }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "#0891b2", margin: "0 0 5px" }}>{t.impact}</p>
                    <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.5, margin: 0 }}>{dd.impact}</p>
                  </div>
                  <div style={{ background: "#fffbeb", borderRadius: 10, padding: 12, borderLeft: "3px solid #d97706" }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "#d97706", margin: "0 0 5px" }}>{t.vigilance}</p>
                    <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.5, margin: 0 }}>{dd.vigilance}</p>
                  </div>
                </div>
              </div>
            );
          })}

          {tab === "axes" && couches.map(c => {
            const sc = coucheScores[c.id]||0;
            const p = getProfile(sc);
            const col = PROFILE_COLOR[p];
            const cDimsArr = dims.filter(d => c.dims.includes(d.id));
            const insight = getCoucheInsight(c.id, dimScores);
            return (
              <div key={c.id} style={sCard}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 20 }}>{c.emoji}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{c.name}</span>
                    </div>
                    <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 8px 28px" }}>{c.desc}</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginLeft: 28 }}>
                      {cDimsArr.map(d => (
                        <span key={d.id} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: "#f3f4f6", color: "#374151" }}>
                          {d.emoji} {d.name} — <strong style={{ color: PROFILE_COLOR[getProfile(dimScores[d.id]||0)] }}>{(dimScores[d.id]||0).toFixed(1)}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                  <Ring score={sc} max={10} size={62} />
                </div>
                <div style={sBarBg}><div style={sBar(sc*10, col)} /></div>
                <div style={{ background: col+"12", borderRadius: 10, padding: 14, marginTop: 12 }}>
                  <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: 0 }}>{coucheData[c.id][p]}</p>
                </div>
                {insight && (
                  <div style={{ background: "#fef3c7", borderRadius: 10, padding: 12, marginTop: 10, borderLeft: "3px solid #f59e0b" }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "#b45309", margin: "0 0 4px" }}>{t.imbalanceLabel}</p>
                    <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.5, margin: 0 }}>{insight}</p>
                  </div>
                )}
              </div>
            );
          })}

          {tab === "synthese" && (
            <div>
              <div style={{ ...sCard, background: PROFILE_COLOR[gp]+"10", border: `1.5px solid ${PROFILE_COLOR[gp]}30` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: PROFILE_COLOR[gp] }}>{profileNames[gp]}</span>
                  <span style={sPill(PROFILE_COLOR[gp])}>Score {Math.round(globalScore)}/100</span>
                </div>
                <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.65, margin: 0 }}>{globalProfiles[gp]}</p>
              </div>

              <div style={sCard}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: "0 0 14px" }}>{t.top3}</h3>
                {sorted.slice(0,3).map(d => {
                  const sc = dimScores[d.id]||0;
                  const p = getProfile(sc);
                  return (
                    <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11 }}>
                      <span style={{ fontSize: 16 }}>{d.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{d.name}</span>
                            <span style={{ fontSize: 10, color: PROFILE_COLOR[p], marginLeft: 8, fontStyle: "italic" }}>{dimData[d.id][p].label}</span>
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: PROFILE_COLOR[p] }}>{sc.toFixed(1)}/10</span>
                        </div>
                        <div style={sBarBg}><div style={sBar(sc*10, PROFILE_COLOR[p])} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={sCard}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: "0 0 14px" }}>{t.dev3}</h3>
                {sorted.slice(-3).reverse().map(d => {
                  const sc = dimScores[d.id]||0;
                  const p = getProfile(sc);
                  return (
                    <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11 }}>
                      <span style={{ fontSize: 16 }}>{d.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{d.name}</span>
                            <span style={{ fontSize: 10, color: PROFILE_COLOR[p], marginLeft: 8, fontStyle: "italic" }}>{dimData[d.id][p].label}</span>
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: PROFILE_COLOR[p] }}>{sc.toFixed(1)}/10</span>
                        </div>
                        <div style={sBarBg}><div style={sBar(sc*10, PROFILE_COLOR[p])} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ ...sCard, background: `linear-gradient(135deg,${G.bg},${G.brown})`, textAlign: "center" }}>
                <p style={{ color: G.amber, fontSize: 11, margin: "0 0 5px" }}>{t.ctaLabel}</p>
                <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>{t.ctaTitle}</h3>
                <p style={{ color: "#9ca3af", fontSize: 12, margin: "0 0 14px", lineHeight: 1.6 }}>{t.ctaBody}</p>
                <a href="https://qualitystreet.fr" target="_blank" rel="noreferrer" style={{ display: "inline-block", padding: "10px 24px", borderRadius: 10, background: `linear-gradient(135deg,${G.gold},${G.amber})`, color: "#fff", fontSize: 13, fontWeight: 700, textDecoration: "none" }}>{t.ctaBtn}</a>
              </div>
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <button onClick={() => { setView("landing"); setScores(null); setAnswers({}); }} style={{ ...sGhostBtn, border: "1px solid #e5e7eb", padding: "8px 20px", borderRadius: 8 }}>{t.backHome}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ADMIN LOGIN
  if (view === "admin-login") return (
    <div style={{ ...sPage, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 360, width: "100%" }}>
        <button onClick={() => setView("landing")} style={sGhostBtn}>← Retour</button>
        <div style={{ ...sCard, marginTop: 16 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 6px" }}>Accès Administrateur</h2>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 22px" }}>Réservé à JC Grosjean et aux spécialistes RH</p>
          <input type="email" placeholder="Email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} onKeyDown={e => e.key==="Enter" && adminLogin()}
            style={{ ...sInput, marginBottom: 10 }} autoComplete="username" />
          <input type="password" placeholder="Mot de passe" value={adminPw} onChange={e => setAdminPw(e.target.value)} onKeyDown={e => e.key==="Enter" && adminLogin()} autoComplete="current-password"
            style={{ ...sInput, marginBottom: 8, borderColor: adminErr ? "#dc2626" : "#d1d5db" }} />
          {adminErr && <p style={{ fontSize: 12, color: "#dc2626", margin: "0 0 10px" }}>{adminErr}</p>}
          <button onClick={adminLogin} style={sPrimBtn(false)}>Accéder →</button>
        </div>
      </div>
    </div>
  );

  // ADMIN
  if (view === "admin") {
    if (selected) {
      const sc = selected.scores;
      const gp = getGlobalProfile(sc.globalScore);
      const sorted = [...dims].sort((a,b) => (sc.dimScores[b.id]||0) - (sc.dimScores[a.id]||0));
      return (
        <div style={sPage}>
          <div style={{ background: "#fff", borderBottom: "1px solid #f0f0f0", padding: "14px 20px", display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setSelected(null)} style={sGhostBtn}>{t.backList}</button>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{selected.name}</span>
            <span style={sPill(PROFILE_COLOR[gp])}>{profileNames[gp]}</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => generatePDF(selected)} style={{ padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", background: `linear-gradient(135deg,${G.gold},${G.amber})`, color: "#fff", fontSize: 13, fontWeight: 600 }}>
              {t.generatePDF}
            </button>
          </div>

          <div style={{ background: `linear-gradient(135deg,${G.bg},${G.brown})`, padding: "20px 24px 16px", textAlign: "center" }}>
            <h2 style={{ color: G.gold, fontSize: 18, fontWeight: 700, margin: "0 0 2px", fontFamily: "Georgia,serif" }}>{selected.name}</h2>
            {(selected.org||selected.fn) && <p style={{ color: "#9ca3af", fontSize: 11, margin: "0 0 14px" }}>{[selected.fn,selected.org].filter(Boolean).join(" · ")}</p>}
            <div style={{ display: "flex", justifyContent: "center", gap: 20, alignItems: "center", marginBottom: 10 }}>
              <div style={{ textAlign: "center" }}><Ring score={sc.globalScore} max={100} size={88} /><p style={{ color: "#9ca3af", fontSize: 10, margin: "4px 0 0" }}>{t.globalScore}</p></div>
              <div style={{ textAlign: "center" }}><Ring score={sc.coherence} max={100} size={68} /><p style={{ color: "#9ca3af", fontSize: 10, margin: "4px 0 0" }}>{t.coherence}</p></div>
            </div>
          </div>

          <div style={{ display: "flex", background: "#fff", borderBottom: "1px solid #f0f0f0", overflowX: "auto" }}>
            {[["overview",t.tabOverview],["dimensions",t.tabDimensions],["axes",t.tabAxes],["synthese",t.tabSynthese]].map(([id,label]) => (
              <button key={id} onClick={() => setTab(id)} style={{ padding: "12px 16px", border: "none", borderBottom: `2px solid ${tab===id ? G.gold : "transparent"}`, background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: tab===id ? G.gold : "#6b7280", whiteSpace: "nowrap" }}>{label}</button>
            ))}
          </div>

          <div style={{ maxWidth: 680, margin: "0 auto", padding: "14px" }}>

            {tab === "overview" && (
              <div>
                <div style={sCard}><h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: "#111827" }}>{t.radarTitle}</h3><AgileRadar dimScores={sc.dimScores} dims={dims} /></div>
                <div style={sCard}>
                  <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#111827" }}>{t.scoresTitle}</h3>
                  {dims.map(d => {
                    const dsc = sc.dimScores[d.id]||0;
                    const p = getProfile(dsc);
                    const dd = dimData[d.id][p];
                    return (
                      <div key={d.id} style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, alignItems: "center" }}>
                          <span style={{ fontSize: 13, color: "#374151", display: "flex", alignItems: "center", gap: 6 }}>
                            {d.emoji} {d.name} {d.weight===2 && <span style={{ color: G.gold, fontSize: 11 }}>★</span>}
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 10, color: PROFILE_COLOR[p], fontStyle: "italic" }}>{dd.label}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: PROFILE_COLOR[p] }}>{dsc.toFixed(1)}</span>
                          </div>
                        </div>
                        <div style={sBarBg}><div style={sBar(dsc*10, PROFILE_COLOR[p])} /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === "dimensions" && dims.map(d => {
              const dsc = sc.dimScores[d.id]||0;
              const p = getProfile(dsc);
              const col = PROFILE_COLOR[p];
              const dd = dimData[d.id][p];
              return (
                <div key={d.id} style={sCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 22 }}>{d.emoji}</span>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{d.name}</span>
                          {d.weight===2 && <span style={{ ...sPill(G.gold), fontSize: 10 }}>{t.foundatriceLabel}</span>}
                        </div>
                        <span style={{ fontSize: 12, fontStyle: "italic", color: col }}>{dd.label}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}><Ring score={dsc} max={10} size={58} /></div>
                  </div>
                  <div style={sBarBg}><div style={sBar(dsc*10, col)} /></div>
                  <div style={{ background: col+"12", borderRadius: 10, padding: 14, marginTop: 12 }}>
                    <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.65, margin: 0 }}>{dd.desc}</p>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                    <div style={{ background: "#f0f9ff", borderRadius: 10, padding: 12, borderLeft: "3px solid #0891b2" }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: "#0891b2", margin: "0 0 5px" }}>{t.impact}</p>
                      <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.5, margin: 0 }}>{dd.impact}</p>
                    </div>
                    <div style={{ background: "#fffbeb", borderRadius: 10, padding: 12, borderLeft: "3px solid #d97706" }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: "#d97706", margin: "0 0 5px" }}>{t.vigilance}</p>
                      <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.5, margin: 0 }}>{dd.vigilance}</p>
                    </div>
                  </div>
                </div>
              );
            })}

            {tab === "axes" && couches.map(c => {
              const csc = coucheScores_admin(c.id, sc.dimScores);
              const p = getProfile(csc);
              const col = PROFILE_COLOR[p];
              const cDimsArr = dims.filter(d => c.dims.includes(d.id));
              const insight = getLangCoucheInsight(c.id, sc.dimScores);
              return (
                <div key={c.id} style={sCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 20 }}>{c.emoji}</span>
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{c.name}</span>
                      </div>
                      <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 8px 28px" }}>{c.desc}</p>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginLeft: 28 }}>
                        {cDimsArr.map(d => (
                          <span key={d.id} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: "#f3f4f6", color: "#374151" }}>
                            {d.emoji} {d.name} — <strong style={{ color: PROFILE_COLOR[getProfile(sc.dimScores[d.id]||0)] }}>{(sc.dimScores[d.id]||0).toFixed(1)}</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                    <Ring score={csc} max={10} size={62} />
                  </div>
                  <div style={sBarBg}><div style={sBar(csc*10, col)} /></div>
                  <div style={{ background: col+"12", borderRadius: 10, padding: 14, marginTop: 12 }}>
                    <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: 0 }}>{coucheData[c.id][p]}</p>
                  </div>
                  {insight && (
                    <div style={{ background: "#fef3c7", borderRadius: 10, padding: 12, marginTop: 10, borderLeft: "3px solid #f59e0b" }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: "#b45309", margin: "0 0 4px" }}>⚡ Déséquilibre détecté</p>
                      <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.5, margin: 0 }}>{insight}</p>
                    </div>
                  )}
                </div>
              );
            })}

            {tab === "synthese" && (
              <div>
                <div style={{ ...sCard, background: PROFILE_COLOR[gp]+"10", border: `1.5px solid ${PROFILE_COLOR[gp]}30` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: PROFILE_COLOR[gp] }}>{profileNames[gp]}</span>
                    <span style={sPill(PROFILE_COLOR[gp])}>Score {Math.round(sc.globalScore)}/100</span>
                  </div>
                  <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.65, margin: 0 }}>{globalProfiles[gp]}</p>
                </div>
                <div style={sCard}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: "0 0 14px" }}>{t.strengths}</h3>
                  {sorted.slice(0,3).map(d => {
                    const dsc = sc.dimScores[d.id]||0;
                    const p = getProfile(dsc);
                    return (
                      <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11 }}>
                        <span style={{ fontSize: 16 }}>{d.emoji}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <div>
                              <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{d.name}</span>
                              <span style={{ fontSize: 10, color: PROFILE_COLOR[p], marginLeft: 8, fontStyle: "italic" }}>{dimData[d.id][p].label}</span>
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: PROFILE_COLOR[p] }}>{dsc.toFixed(1)}/10</span>
                          </div>
                          <div style={sBarBg}><div style={sBar(dsc*10, PROFILE_COLOR[p])} /></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={sCard}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: "0 0 14px" }}>{t.devAxes}</h3>
                  {sorted.slice(-3).reverse().map(d => {
                    const dsc = sc.dimScores[d.id]||0;
                    const p = getProfile(dsc);
                    return (
                      <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11 }}>
                        <span style={{ fontSize: 16 }}>{d.emoji}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <div>
                              <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{d.name}</span>
                              <span style={{ fontSize: 10, color: PROFILE_COLOR[p], marginLeft: 8, fontStyle: "italic" }}>{dimData[d.id][p].label}</span>
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: PROFILE_COLOR[p] }}>{dsc.toFixed(1)}/10</span>
                          </div>
                          <div style={sBarBg}><div style={sBar(dsc*10, PROFILE_COLOR[p])} /></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        </div>
      );
    }

    const avgGlobal = responses.length > 0 ? responses.reduce((s,r) => s+r.scores.globalScore, 0)/responses.length : 0;
    const pCounts = { pionnier:0, avance:0, explorateur:0, eveil:0 };
    responses.forEach(r => { pCounts[getGlobalProfile(r.scores.globalScore)]++; });
    return (
      <div style={sPage}>
        <div style={{ background: "#fff", borderBottom: "1px solid #f0f0f0", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: 0 }}>{t.adminTitle}</h1>
            <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>{responses.length} réponse(s)</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={loadResponses} style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", fontSize: 13 }}>↻</button>
            <button onClick={() => setView("landing")} style={sGhostBtn}>Quitter</button>
          </div>
        </div>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 18 }}>
            {[
              { l: t.participants, v: responses.length, c: G.gold },
              { l: t.avgScore, v: Math.round(avgGlobal)+"/100", c: G.gold },
              { l: t.pioneers, v: pCounts.pionnier, c: "#16a34a" },
              { l: t.awakening, v: pCounts.eveil, c: "#dc2626" },
            ].map(m => (
              <div key={m.l} style={{ background: "#fff", borderRadius: 12, padding: 14, textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,.07)" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: m.c }}>{m.v}</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{m.l}</div>
              </div>
            ))}
          </div>
          {responses.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📊</div>
              <p>Aucune réponse enregistrée pour le moment.</p>
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 3px rgba(0,0,0,.07)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ background: "#f9fafb" }}>
                  <tr>{["Participant","Date","Score","Profil","Cohérence",""].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {responses.map(r => {
                    const p = getGlobalProfile(r.scores.globalScore);
                    return (
                      <tr key={r.key} onClick={() => setSelected(r)} style={{ borderTop: "1px solid #f3f4f6", cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background="#fafafa"}
                        onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{r.name}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.email}</div>
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: 12, color: "#6b7280" }}>{r.date}</td>
                        <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 700, color: G.gold }}>{Math.round(r.scores.globalScore)}</td>
                        <td style={{ padding: "12px 14px" }}><span style={sPill(PROFILE_COLOR[p])}>{profileNames[p]}</span></td>
                        <td style={{ padding: "12px 14px", fontSize: 12, color: "#374151" }}>{r.scores.coherence}</td>
                        <td style={{ padding: "12px 14px", fontSize: 12, color: "#9ca3af", textAlign: "right" }}>Voir →</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

// helper for admin detail
function coucheScores_admin(coucheId, dimScores) {
  const c = COUCHES.find(x => x.id === coucheId);
  return c.dims.map(id => dimScores[id]||0).reduce((a,b) => a+b, 0) / c.dims.length;
}
